import { Component, OnInit, ViewChild, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GoogleMap, MapPolygon } from '@angular/google-maps';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

// PrimeNG Components
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageModule } from 'primeng/message';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AvatarModule } from 'primeng/avatar';
import { ChipModule } from 'primeng/chip';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { MultiSelectModule } from 'primeng/multiselect';
import { DrawerModule } from 'primeng/drawer';


// Services and Models
import { TerritoryService } from '../../../core/services/territory.service';
import { PagedUsersResponse, UserService } from '../../../core/services/user.service';
import { AuthService } from '../../../core/services/auth';
import { SubscriptionContextService } from '../../../core/services/subscription-context.service';
import { ProductService, Product } from '../../../core/services/product.service';
import { SubscriberService } from '../../../core/services/subscriber.service';
import { SETTINGS } from '../../../core/config/common.settings';
import { 
    Territory, 
    CreateTerritoryRequest,
    UpdateTerritoryRequest,
    TerritoryFilters 
} from '../../../core/models/territory.model';
import { User } from '../../../core/services/user.service';

// PrimeNG Services
import { MessageService } from 'primeng/api';
import { environment } from '../../../../environments/environment';

interface Country {
    name: string;
    code: string;
    bounds: google.maps.LatLngBoundsLiteral;
    center: google.maps.LatLngLiteral;
    zoom: number;
}

interface SavedPolygon {
    id: string;
    name: string;
    path: google.maps.LatLngLiteral[];
    region: string;
    status: string;
}

@Component({
    selector: 'app-add-territory',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        GoogleMap,
        MapPolygon,
        CardModule,
        ButtonModule,
        InputTextModule,
        SelectModule,
        ToggleSwitchModule,
        MessageModule,
        ToastModule,
        ProgressSpinnerModule,
        AvatarModule,
        ChipModule,
        InputGroupModule,
        InputGroupAddonModule,
        MultiSelectModule,
        DrawerModule
    ],
    providers: [MessageService],
    templateUrl: './add-territory.html',
    styleUrl: './add-territory.scss'
})
export class AddTerritoryComponent implements OnInit, OnDestroy {
    @ViewChild('mapRef', { static: false }) mapRef!: GoogleMap;
    @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;
    @ViewChild('rightPanel', { static: false }) rightPanel!: ElementRef<HTMLElement>;
    @ViewChild('mapControlsCard', { static: false }) mapControlsCard!: ElementRef<HTMLElement>;

    selectedSubscriberId: string | null = null;

    // Map properties
    zoom = 7;
    center: google.maps.LatLngLiteral = { lat: 7.8731, lng: 80.7718 }; // Default to Sri Lanka
    options: google.maps.MapOptions = {
        mapTypeId: 'roadmap',
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        scrollwheel: false, // Disabled to handle Ctrl+Scroll manually
        disableDoubleClickZoom: true,
        minZoom: 3,
        maxZoom: 20
    };

    // Component state
    loading = false;
    mapLoading = true;
    isDrawingMode = false;
    isEditMode = false;
    isPolygonEditingMode = false;
    mapControlsVisible = true;
    /** Draggable map controls card position (px from top/left of right panel). */
    mapControlsTop = 16;
    mapControlsLeft = 16;
    private isDraggingMapControls = false;
    private mapControlsDragStart = { x: 0, y: 0, left: 0, top: 0 };
    private mapControlsDragListeners: { move: (e: MouseEvent) => void; up: (e: MouseEvent) => void } | null = null;
    editingTerritoryId: string | null = null;
    showUserDrawer = false;
    snapToEdge = true; // Magnetizing feature toggle
    snapThreshold = 0.00135; // Distance threshold in degrees (approximately 150 meters) - aggressive snapping to eliminate gaps
    snapDistanceMeters = 150; // User-friendly distance in meters - increased for better gap elimination
    /** When a snapped point is within this distance of an existing vertex, use that exact vertex to avoid visible gaps between polygons. */
    private readonly vertexConsolidationMeters = 2;
    /**
     * Cap the effective distance for intermediate point processing (enrichment, gap-filling, refinement).
     * Vertex-to-vertex snapping uses the full user-specified snapDistanceMeters,
     * but processing steps that add intermediate points or move existing points toward boundaries
     * are capped to prevent aggressive polygon deformation at high snap distances.
     */
    private readonly maxProcessingDistanceMeters = 2000;
    nodeDensification = true; // Enable automatic node densification for better boundary accuracy

    // Forms
    territoryForm: FormGroup;

    // Drawing state
    currentPolygon: google.maps.LatLngLiteral[] = [];
    drawingManager?: google.maps.drawing.DrawingManager;
    editingPolygon?: google.maps.Polygon;
    
    // Polygon editing state
    private editingOverlay?: google.maps.Polygon;
    private editingOverlayListeners: google.maps.MapsEventListener[] = [];
    private lastValidPath: google.maps.LatLngLiteral[] = [];
    /** Guard: true while we are programmatically reverting the overlay path (setPath). Prevents set_at/dragend from re-running validation and overwriting state. */
    private isRevertingOverlay = false;

    // Available users for assignment
    availableUsers: User[] = [];
    selectedUsers: User[] = [];
    searchQuery = '';
    userSearchQuery = '';

    // Existing territories in the selected region
    existingTerritories: Territory[] = [];
    selectedRegionObject: any = null; // Full region object
    shouldRenderMap = false;
    
    // Country border visualization
    countryBorderRectangle?: google.maps.Rectangle;
    
    regions: any[] = [];
    allRegions = SETTINGS.REGIONS;

    constructor(
        private territoryService: TerritoryService,
        private userService: UserService,
        private authService: AuthService,
        private subscriptionContextService: SubscriptionContextService,
        private productService: ProductService,
        private subscriberService: SubscriberService,
        private fb: FormBuilder,
        private messageService: MessageService,
        private ngZone: NgZone,
        private router: Router
    ) {
        this.territoryForm = this.fb.group({
            name: ['', [Validators.required, Validators.minLength(2)]],
            region: [null, Validators.required], // Changed from '' to null
            isActive: [true, Validators.required],
            subscriptionId: ['']
        });
    }

    ngOnInit(): void {
        console.log('AddTerritoryComponent: ngOnInit called');
        this.loadAvailableUsers();
        
        // Check edit mode and set region from URL parameters
        this.checkEditMode();
        if (!this.isEditMode) {
            this.mapLoading = false;
            this.shouldRenderMap = true;
        }
        
        this.setupFormListeners();
        this.checkDrawingLibraryAvailability();
        this.selectedSubscriberId = this.subscriptionContextService.getSelectedSubscriberId(); 
        const currentUser = this.authService.getLoggedInUser();
        console.log('AddTerritoryComponent: Current user in ngOnInit:', currentUser);
        console.log('AddTerritoryComponent: Subscriber ID:', currentUser?.subscriberID);
        
        // Load subscriber's operational regions to filter the regions list
        this.loadSubscriberOperationalRegions();
        
        // Initialize snap threshold from meters value
        this.snapThreshold = this.snapDistanceMeters / 111320;
    }

    ngAfterViewInit(): void {
        this.initializeMap();
    }

    ngOnDestroy(): void {
        this.removeMapControlsDragListeners();
        if (this.drawingManager) {
            this.drawingManager.setMap(null);
        }
        if (this.countryBorderRectangle) {
            this.countryBorderRectangle.setMap(null);
        }
        this.cleanupEditingOverlay();
        this.removeMouseTracking();
        this.removeDrawingSnapping();
        this.removeWheelListener();
    }

    onMapControlsDragStart(e: MouseEvent): void {
        if (this.isDraggingMapControls) return;
        e.preventDefault();
        this.isDraggingMapControls = true;
        this.mapControlsDragStart = {
            x: e.clientX,
            y: e.clientY,
            left: this.mapControlsLeft,
            top: this.mapControlsTop
        };
        const move = (ev: MouseEvent) => {
            if (!this.isDraggingMapControls) return;
            const dx = ev.clientX - this.mapControlsDragStart.x;
            const dy = ev.clientY - this.mapControlsDragStart.y;
            let left = this.mapControlsDragStart.left + dx;
            let top = this.mapControlsDragStart.top + dy;
            const panel = this.rightPanel?.nativeElement;
            const card = this.mapControlsCard?.nativeElement;
            if (panel && card) {
                const panelRect = panel.getBoundingClientRect();
                const cardRect = card.getBoundingClientRect();
                const maxLeft = panelRect.width - cardRect.width;
                const maxTop = panelRect.height - cardRect.height;
                left = Math.max(0, Math.min(left, maxLeft));
                top = Math.max(0, Math.min(top, maxTop));
            }
            this.ngZone.run(() => {
                this.mapControlsLeft = left;
                this.mapControlsTop = top;
            });
        };
        const up = () => {
            this.isDraggingMapControls = false;
            this.removeMapControlsDragListeners();
        };
        this.mapControlsDragListeners = { move, up };
        document.addEventListener('mousemove', move);
        document.addEventListener('mouseup', up);
    }

    private removeMapControlsDragListeners(): void {
        if (this.mapControlsDragListeners) {
            document.removeEventListener('mousemove', this.mapControlsDragListeners.move);
            document.removeEventListener('mouseup', this.mapControlsDragListeners.up);
            this.mapControlsDragListeners = null;
        }
    }

    private cleanupEditingOverlay(): void {
        if (this.editingOverlay) {
            this.editingOverlay.setMap(null);
            this.editingOverlayListeners.forEach(listener => listener.remove());
            this.editingOverlayListeners = [];
        }
    }

    private mouseTrackListener?: google.maps.MapsEventListener;
    private currentDrawingPolygon?: google.maps.Polygon;
    
    // Wheel event listener for Ctrl+Scroll zoom
    private wheelListener?: () => void;

    private setupMouseTracking(): void {
        if (!this.mapRef?.googleMap) return;

        // Listen for clicks to track drawing progress
        this.mouseTrackListener = google.maps.event.addListener(
            this.mapRef.googleMap,
            'click',
            (event: google.maps.MapMouseEvent) => {
                if (!this.isDrawingMode || !this.snapToEdge || !event.latLng) return;

                // Get the current polygon being drawn
                // We need to track this through the drawing manager's internal state
                // For now, we'll just show visual feedback when near an edge
                const point: google.maps.LatLngLiteral = {
                    lat: event.latLng.lat(),
                    lng: event.latLng.lng()
                };
                const snapped = this.findNearestSnapPoint(point);
                if (snapped) {
                    console.log('Near territory edge - snap available');
                }
            }
        );
    }

    private removeMouseTracking(): void {
        if (this.mouseTrackListener) {
            this.mouseTrackListener.remove();
            this.mouseTrackListener = undefined;
        }
    }

    private setupWheelZoomListener(): void {
        if (!this.mapContainer?.nativeElement || !this.mapRef?.googleMap) {
            // Retry if map container isn't ready yet
            setTimeout(() => this.setupWheelZoomListener(), 100);
            return;
        }

        const mapContainerElement = this.mapContainer.nativeElement;

        // Handle wheel events - disable page scroll when over map, enable zoom with Ctrl/Cmd
        const wheelHandler = (event: WheelEvent) => {
            // Check if the event is within the map container using multiple methods
            const target = event.target as HTMLElement;
            const rect = mapContainerElement.getBoundingClientRect();
            const isOverMapByCoordinates = 
                event.clientX >= rect.left &&
                event.clientX <= rect.right &&
                event.clientY >= rect.top &&
                event.clientY <= rect.bottom;

            // Also check if target is within map container (handles nested elements)
            const isOverMapByElement = mapContainerElement.contains(target);

            // Use composedPath for better detection through shadow DOM
            const isOverMapByPath = event.composedPath().some((node) => {
                return node === mapContainerElement || 
                       (node instanceof Node && mapContainerElement.contains(node));
            });

            const isOverMapElement = isOverMapByCoordinates || isOverMapByElement || isOverMapByPath;
            
            // If cursor is over map area, prevent page scrolling
            if (isOverMapElement) {
                // Prevent page scrolling when over map
                event.preventDefault();
                event.stopPropagation();
                
                // If Ctrl/Cmd is pressed, handle map zoom
                if (event.ctrlKey || event.metaKey) {
                    event.stopImmediatePropagation();

                    // Calculate zoom delta based on wheel direction
                    const zoomDelta = event.deltaY > 0 ? -1 : 1;
                    const newZoom = Math.max(
                        this.options.minZoom || 3,
                        Math.min(
                            this.options.maxZoom || 20,
                            this.zoom + zoomDelta
                        )
                    );

                    if (newZoom !== this.zoom) {
                        this.ngZone.run(() => {
                            this.zoom = newZoom;
                        });
                    }
                }
                // If Ctrl/Cmd is not pressed, just prevent page scrolling (map won't zoom)
            }
            // If cursor is not over map, allow normal page scrolling
        };

        // Attach wheel listener to document to catch all wheel events
        // Use capture phase to intercept before browser handles it
        document.addEventListener('wheel', wheelHandler, { passive: false, capture: true });

        // Also attach to the map container itself as a backup
        mapContainerElement.addEventListener('wheel', wheelHandler, { passive: false, capture: true });

        // Store cleanup function
        this.wheelListener = () => {
            document.removeEventListener('wheel', wheelHandler, { capture: true } as EventListenerOptions);
            mapContainerElement.removeEventListener('wheel', wheelHandler, { capture: true } as EventListenerOptions);
        };
    }

    private removeWheelListener(): void {
        if (this.wheelListener) {
            this.wheelListener();
            this.wheelListener = undefined;
        }
    }

    private applySnapping(path: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
        if (!this.snapToEdge || this.existingTerritories.length === 0 || path.length < 2) {
            console.log('Snapping disabled or no existing territories');
            return path;
        }

        console.log('Applying advanced snapping algorithm...');
        
        console.log('Starting comprehensive boundary alignment...');
        
        // Step 1: Initial vertex snapping and enrichment
        let processedPath = this.enrichWithBoundaryAlignment(path);
        console.log(`Step 1 complete: ${path.length} → ${processedPath.length} points`);

        // Step 1b: Snap long runs of points along the same boundary segment to that segment (long parallel line)
        processedPath = this.snapLongRunsToBoundarySegments(processedPath);
        console.log(`Step 1b (long parallel): ${processedPath.length} points`);
        
        // Step 2: Apply curve smoothing to shared boundaries
        processedPath = this.smoothSharedBoundaries(processedPath);
        console.log(`Step 2 complete: ${processedPath.length} points`);
        
        // Step 3: Fill small gaps by expanding towards boundaries (iterative)
        processedPath = this.fillSmallGaps(processedPath);
        console.log(`Step 3 complete: ${processedPath.length} points`);
        
        // Step 4: Edge-to-edge alignment for parallel boundaries
        processedPath = this.alignEdgesWithBoundaries(processedPath);
        console.log(`Step 4 complete: ${processedPath.length} points`);
        
        // Step 5: Final gap detection and closure
        processedPath = this.closeRemainingGaps(processedPath);
        console.log(`Step 5 complete: ${processedPath.length} points`);
        
        // Step 6: Boundary quality check and refinement
        processedPath = this.refineBoundaryQuality(processedPath);
        console.log(`Step 6 complete: ${processedPath.length} points`);
        
        // Step 7: Final cleanup of duplicates and validation
        processedPath = this.cleanupDuplicatePoints(processedPath);
        console.log(`Final result: ${path.length} → ${processedPath.length} points`);
        
        return processedPath;
    }
    
    private enrichWithBoundaryAlignment(path: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
        const enriched: google.maps.LatLngLiteral[] = [];
        // Use capped processing distance for intermediate point enrichment to prevent
        // aggressive polygon deformation at high snap distances (e.g., 10km)
        const snapDistance = this.getProcessingDistance();
        
        for (let i = 0; i < path.length; i++) {
            const p1 = path[i];
            const p2 = path[(i + 1) % path.length];
            
            // Check if there's a nearby boundary for this edge
            const hasNearbyBoundary = this.hasNearbyBoundaryForEdge(p1, p2);
            
            // Only add the current point if there's a snap or no nearby boundary
            const snappedP1 = this.findNearestSnapPoint(p1) || p1;
            
            // Only add intermediate points if there's a nearby boundary
            if (hasNearbyBoundary) {
                enriched.push(snappedP1);
                
                // Add intermediate points only where boundaries exist
                const segments = 8;
                for (let j = 1; j < segments; j++) {
                    const t = j / segments;
                    const midPoint: google.maps.LatLngLiteral = {
                        lat: p1.lat + (p2.lat - p1.lat) * t,
                        lng: p1.lng + (p2.lng - p1.lng) * t
                    };
                    
                    // Only process if this midpoint is near a boundary
                    const midNearbyBoundary = this.findNearestBoundaryPoint(midPoint);
                    if (midNearbyBoundary) {
                        const midDistance = this.haversineDistance(midPoint, midNearbyBoundary);
                        if (midDistance < snapDistance * 1.5) {
                            // Try snapping the midpoint
                            const snappedMid = this.findNearestSnapPoint(midPoint);
                            
                            if (snappedMid) {
                                if (enriched.length === 0 || 
                                    this.haversineDistance(snappedMid, enriched[enriched.length - 1]) > 1.0) {
                                    enriched.push(snappedMid);
                                }
                            } else {
                                enriched.push(midPoint);
                            }
                        } else {
                            enriched.push(midPoint);
                        }
                    } else {
                        // No boundary nearby - keep original point
                        enriched.push(midPoint);
                    }
                }
            } else {
                // No nearby boundary - keep edge simple with just endpoints
                enriched.push(p1);
            }
        }
        
        return enriched;
    }
    
    private hasNearbyBoundaryForEdge(p1: google.maps.LatLngLiteral, p2: google.maps.LatLngLiteral): boolean {
        // Use capped processing distance so edges far from boundaries aren't enriched
        const snapDistance = this.getProcessingDistance();
        
        // Sample edge at 3 points
        for (let i = 0; i <= 2; i++) {
            const t = i / 2;
            const point: google.maps.LatLngLiteral = {
                lat: p1.lat + (p2.lat - p1.lat) * t,
                lng: p1.lng + (p2.lng - p1.lng) * t
            };
            
            const nearestBoundary = this.findNearestBoundaryPoint(point);
            if (nearestBoundary) {
                const distance = this.haversineDistance(point, nearestBoundary);
                if (distance < snapDistance * 1.2) {
                    return true; // Found nearby boundary
                }
            }
        }
        
        return false;
    }
    
    private shouldAddIntermediatePoints(p1: google.maps.LatLngLiteral, p2: google.maps.LatLngLiteral): boolean {
        // Sample more points along the edge for better detection
        const samples = 5; // Increased from 3
        let nearBoundaryCount = 0;
        
        for (let i = 1; i <= samples; i++) {
            const t = i / (samples + 1);
            const samplePoint: google.maps.LatLngLiteral = {
                lat: p1.lat + (p2.lat - p1.lat) * t,
                lng: p1.lng + (p2.lng - p1.lng) * t
            };
            
            if (this.hasNearbyBoundary(samplePoint)) {
                nearBoundaryCount++;
            }
        }
        
        // Add intermediate points if at least 40% of samples are near boundaries
        return nearBoundaryCount >= (samples * 0.4);
    }
    
    private hasNearbyBoundary(point: google.maps.LatLngLiteral): boolean {
        for (const territory of this.existingTerritories) {
            if (this.isEditMode && this.editingTerritoryId && territory._id === this.editingTerritoryId) {
                continue;
            }
            
            const nearest = this.findClosestPointOnAnyEdge(point, territory.coordinates);
            if (nearest) {
                const distance = this.haversineDistance(point, nearest);
                // Use capped processing distance for boundary detection
                if (distance < this.getProcessingDistance()) {
                    return true;
                }
            }
        }
        return false;
    }
    
    private smoothSharedBoundaries(path: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
        if (path.length < 3) return path;
        
        const smoothed: google.maps.LatLngLiteral[] = [];
        
        for (let i = 0; i < path.length; i++) {
            const prev = path[(i - 1 + path.length) % path.length];
            const curr = path[i];
            const next = path[(i + 1) % path.length];
            
            // Apply slight smoothing if point is on a shared boundary
            const isOnSharedBoundary = this.isPointOnSharedBoundary(curr);
            
            if (isOnSharedBoundary && i > 0 && i < path.length - 1) {
                // Apply minor smoothing to improve boundary alignment
                const smoothedPoint: google.maps.LatLngLiteral = {
                    lat: curr.lat + (prev.lat + next.lat - 2 * curr.lat) * 0.15,
                    lng: curr.lng + (prev.lng + next.lng - 2 * curr.lng) * 0.15
                };
                smoothed.push(smoothedPoint);
            } else {
                smoothed.push(curr);
            }
        }
        
        return smoothed;
    }
    
    private isPointOnSharedBoundary(point: google.maps.LatLngLiteral): boolean {
        for (const territory of this.existingTerritories) {
            if (this.isEditMode && this.editingTerritoryId && territory._id === this.editingTerritoryId) {
                continue;
            }
            
            if (this.isPointOnBoundary(point, territory.coordinates)) {
                return true;
            }
        }
        return false;
    }
    
    private fillSmallGaps(path: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
        // Cap gap threshold to prevent moving points huge distances at high snap settings
        const gapThreshold = Math.min(this.snapThreshold * 2.0 * 111320, this.maxProcessingDistanceMeters);
        
        let processedPath = [...path];
        let iterations = 0;
        const maxIterations = 5;
        
        while (iterations < maxIterations) {
            processedPath = processedPath.map(point => {
                const nearestBoundary = this.findNearestBoundaryPoint(point);
                if (nearestBoundary) {
                    const distance = this.haversineDistance(point, nearestBoundary);
                    // Only fill gaps if point is within gap threshold
                    if (distance > 0 && distance < gapThreshold) {
                        const moveRatio = Math.min(0.95, distance / gapThreshold * 0.8);
                        return {
                            lat: point.lat + (nearestBoundary.lat - point.lat) * moveRatio,
                            lng: point.lng + (nearestBoundary.lng - point.lng) * moveRatio
                        };
                    }
                }
                // No nearby boundary - leave point unchanged
                return point;
            });
            
            iterations++;
        }
        
        // Final pass: only snap if very close to boundary
        return processedPath.map(point => {
            const nearestBoundary = this.findNearestBoundaryPoint(point);
            if (nearestBoundary) {
                const distance = this.haversineDistance(point, nearestBoundary);
                // Only snap if very close (within capped processing distance)
                if (distance < this.getProcessingDistance()) {
                    return nearestBoundary;
                }
            }
            // No nearby boundary or too far - keep original
            return point;
        });
    }
    
    private findNearestBoundaryPoint(point: google.maps.LatLngLiteral): google.maps.LatLngLiteral | null {
        let closestPoint: google.maps.LatLngLiteral | null = null;
        let minDistance = Infinity;

        for (const territory of this.existingTerritories) {
            if (this.isEditMode && this.editingTerritoryId && territory._id === this.editingTerritoryId) {
                continue;
            }

            const nearest = this.findClosestPointOnAnyEdge(point, territory.coordinates);
            if (nearest) {
                const distance = this.haversineDistance(point, nearest);
                if (distance < minDistance) {
                    minDistance = distance;
                    closestPoint = nearest;
                }
            }
        }

        // Prefer exact existing vertex when very close so shared boundaries use same coordinates (no gap)
        if (closestPoint) {
            // Don't return boundary points outside the region bounds
            if (!this.isInRegionBounds(closestPoint)) return null;
            const vertex = this.findExistingVertexWithinDistance(closestPoint, this.vertexConsolidationMeters);
            if (vertex && this.isInRegionBounds(vertex)) return vertex;
            // If vertex exists but is out of bounds, return the in-bounds closestPoint instead
        }
        return closestPoint;
    }
    
    private alignEdgesWithBoundaries(path: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
        // For each vertex, snap it to a boundary if its incoming or outgoing edge aligns to one (one point per vertex).
        const aligned: google.maps.LatLngLiteral[] = [];
        const n = path.length;

        for (let i = 0; i < n; i++) {
            const curr = path[i];
            const prev = path[(i - 1 + n) % n];
            const next = path[(i + 1) % n];

            // Prefer alignment from edge ending at this vertex, then edge starting at this vertex
            const alignmentIn = this.findBestBoundaryAlignment(prev, curr);
            const alignmentOut = this.findBestBoundaryAlignment(curr, next);

            if (alignmentIn) {
                const snapped = this.findExistingVertexWithinDistance(alignmentIn.end, this.vertexConsolidationMeters) || alignmentIn.end;
                aligned.push(snapped);
            } else if (alignmentOut) {
                const snapped = this.findExistingVertexWithinDistance(alignmentOut.start, this.vertexConsolidationMeters) || alignmentOut.start;
                aligned.push(snapped);
            } else {
                aligned.push(this.findNearestSnapPoint(curr) || curr);
            }
        }

        return aligned;
    }
    
    private findBestBoundaryAlignment(
        p1: google.maps.LatLngLiteral, 
        p2: google.maps.LatLngLiteral
    ): { start: google.maps.LatLngLiteral; end: google.maps.LatLngLiteral } | null {
        // Use capped processing distance to prevent projecting vertices onto distant boundaries
        const baseDistance = this.getProcessingDistance();

        // Try to find a boundary edge that's parallel to this edge
        for (const territory of this.existingTerritories) {
            if (this.isEditMode && this.editingTerritoryId && territory._id === this.editingTerritoryId) {
                continue;
            }

            const coords = territory.coordinates;
            if (!coords || coords.length < 2) continue;

            for (let i = 0; i < coords.length; i++) {
                const b1 = coords[i];
                const b2 = coords[(i + 1) % coords.length];

                // Check if boundaries are roughly parallel
                if (this.areEdgesParallel(p1, p2, b1, b2)) {
                    const boundaryLength = this.haversineDistance(b1, b2);
                    // Use larger snap distance for long boundary segments so magnetic works on long parallel lines
                    const maxDistance = boundaryLength > 300 ? baseDistance * 1.5 : baseDistance;
                    const d1 = this.pointToEdgeDistance(p1, b1, b2);
                    const d2 = this.pointToEdgeDistance(p2, b1, b2);

                    if (d1 < maxDistance && d2 < maxDistance) {
                        const projStart = this.closestPointOnEdge(p1, b1, b2);
                        const projEnd = this.closestPointOnEdge(p2, b1, b2);
                        // Don't project if result would be outside region bounds
                        if (!this.isInRegionBounds(projStart) || !this.isInRegionBounds(projEnd)) continue;
                        return { start: projStart, end: projEnd };
                    }
                }
            }
        }

        return null;
    }
    
    private areEdgesParallel(
        e1_start: google.maps.LatLngLiteral,
        e1_end: google.maps.LatLngLiteral,
        e2_start: google.maps.LatLngLiteral,
        e2_end: google.maps.LatLngLiteral
    ): boolean {
        const dx1 = e1_end.lng - e1_start.lng;
        const dy1 = e1_end.lat - e1_start.lat;
        const dx2 = e2_end.lng - e2_start.lng;
        const dy2 = e2_end.lat - e2_start.lat;
        
        const angle1 = Math.atan2(dy1, dx1);
        const angle2 = Math.atan2(dy2, dx2);
        
        const angleDiff = Math.abs(angle1 - angle2);
        const minAngle = Math.min(angleDiff, Math.PI - angleDiff);
        
        // Consider parallel if angle difference is less than 15 degrees
        return minAngle < (15 * Math.PI / 180);
    }

    /**
     * Find the boundary segment (from existing territories) closest to a point.
     * Used to detect "long parallel line" runs so we can snap whole runs to one segment.
     */
    private findClosestBoundarySegment(point: google.maps.LatLngLiteral): { key: string; b1: google.maps.LatLngLiteral; b2: google.maps.LatLngLiteral; distance: number } | null {
        // Cap max detection distance to prevent detecting distant boundaries as "runs"
        const maxDistance = Math.min(this.snapThreshold * 111320 * 1.5, this.maxProcessingDistanceMeters);
        let best: { key: string; b1: google.maps.LatLngLiteral; b2: google.maps.LatLngLiteral; distance: number } | null = null;

        for (const territory of this.existingTerritories) {
            if (this.isEditMode && this.editingTerritoryId && territory._id === this.editingTerritoryId) continue;
            const coords = territory.coordinates;
            if (!coords || coords.length < 2) continue;

            for (let i = 0; i < coords.length; i++) {
                const b1 = coords[i];
                const b2 = coords[(i + 1) % coords.length];
                const closest = this.closestPointOnEdge(point, b1, b2);
                const distance = this.haversineDistance(point, closest);
                if (distance < maxDistance && (!best || distance < best.distance)) {
                    best = { key: `${territory._id}-${i}`, b1, b2, distance };
                }
            }
        }
        return best;
    }

    /**
     * Snap long runs of consecutive points that follow the same boundary segment to exactly
     * that segment (first and last projected onto the segment). This makes the magnetic
     * feature work on long parallel lines: one drawn edge snaps to the full existing edge
     * without overlaps or gaps, without needing to click many points along it.
     */
    private snapLongRunsToBoundarySegments(path: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
        if (path.length < 3) return path;
        // Use capped processing distance for run detection
        const snapDistance = this.getProcessingDistance();

        type Run = { startIdx: number; endIdx: number; key: string; b1: google.maps.LatLngLiteral; b2: google.maps.LatLngLiteral };
        const runs: Run[] = [];
        let runStart = -1;
        let runKey: string | null = null;
        let runB1: google.maps.LatLngLiteral | null = null;
        let runB2: google.maps.LatLngLiteral | null = null;

        for (let i = 0; i < path.length; i++) {
            const seg = this.findClosestBoundarySegment(path[i]);
            if (seg && seg.distance <= snapDistance) {
                if (runKey === seg.key && runB1 && runB2) {
                    // extend run (keep same segment)
                } else {
                    if (runStart >= 0 && runKey && runB1 && runB2 && (runStart < i - 1)) {
                        runs.push({ startIdx: runStart, endIdx: i - 1, key: runKey, b1: runB1, b2: runB2 });
                    }
                    runStart = i;
                    runKey = seg.key;
                    runB1 = seg.b1;
                    runB2 = seg.b2;
                }
            } else {
                if (runStart >= 0 && runKey && runB1 && runB2 && (runStart < i - 1)) {
                    runs.push({ startIdx: runStart, endIdx: i - 1, key: runKey, b1: runB1, b2: runB2 });
                }
                runStart = -1;
                runKey = null;
                runB1 = null;
                runB2 = null;
            }
        }
        if (runStart >= 0 && runKey && runB1 && runB2 && runStart < path.length - 1) {
            runs.push({ startIdx: runStart, endIdx: path.length - 1, key: runKey, b1: runB1, b2: runB2 });
        }

        if (runs.length === 0) return path;

        // Build new path: replace each run with points on the segment. Use exact b1/b2 when very close so both polygons share the same edge (no visible gap).
        const vertexStickMeters = this.vertexConsolidationMeters;
        const result: google.maps.LatLngLiteral[] = [];
        let pos = 0;
        for (const run of runs) {
            while (pos < run.startIdx) result.push(path[pos++]);
            let firstProj = this.closestPointOnEdge(path[run.startIdx], run.b1, run.b2);
            let lastProj = this.closestPointOnEdge(path[run.endIdx], run.b1, run.b2);
            // Skip this run if projected points would be outside region bounds
            if (!this.isInRegionBounds(firstProj) || !this.isInRegionBounds(lastProj)) {
                // Keep original path points for this run instead
                for (let ri = run.startIdx; ri <= run.endIdx; ri++) result.push(path[ri]);
                pos = run.endIdx + 1;
                continue;
            }
            if (this.haversineDistance(firstProj, run.b1) <= vertexStickMeters) firstProj = run.b1;
            else if (this.haversineDistance(firstProj, run.b2) <= vertexStickMeters) firstProj = run.b2;
            if (this.haversineDistance(lastProj, run.b1) <= vertexStickMeters) lastProj = run.b1;
            else if (this.haversineDistance(lastProj, run.b2) <= vertexStickMeters) lastProj = run.b2;
            if (this.haversineDistance(firstProj, lastProj) > 0.5) {
                result.push(firstProj);
                result.push(lastProj);
            } else {
                result.push(firstProj);
            }
            pos = run.endIdx + 1;
        }
        while (pos < path.length) result.push(path[pos++]);

        return result.length >= 3 ? result : path;
    }
    
    private closeRemainingGaps(path: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
        // Cap gap threshold to prevent aggressive deformation at high snap distances
        const gapThreshold = Math.min(this.snapThreshold * 111320 * 1.2, this.maxProcessingDistanceMeters);
        const processedPath: google.maps.LatLngLiteral[] = [];
        
        for (let i = 0; i < path.length; i++) {
            const point = path[i];
            
            const nearestBoundary = this.findNearestBoundaryPoint(point);
            
            if (nearestBoundary) {
                const distance = this.haversineDistance(point, nearestBoundary);
                
                // Only close gaps if very close to boundary
                if (distance < gapThreshold) {
                    processedPath.push(nearestBoundary);
                } else {
                    // Too far from boundary - keep original point
                    processedPath.push(point);
                }
            } else {
                // No nearby boundary - keep original
                processedPath.push(point);
            }
        }
        
        return processedPath;
    }
    
    private refineBoundaryQuality(path: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
        const refined: google.maps.LatLngLiteral[] = [];
        // Use capped processing distance so refinement doesn't pull points too far
        const snapDistance = this.getProcessingDistance();
        
        for (let i = 0; i < path.length; i++) {
            const curr = path[i];
            
            // Check if this point is on a shared boundary
            const isOnSharedBoundary = this.isPointOnSharedBoundary(curr);
            
            if (isOnSharedBoundary) {
                // Point is on boundary - keep it as-is
                refined.push(curr);
            } else {
                // Check distance to nearest boundary
                const nearestBoundary = this.findNearestBoundaryPoint(curr);
                
                if (nearestBoundary) {
                    const distance = this.haversineDistance(curr, nearestBoundary);
                    
                    // Only refine if very close to boundary (within 0.5x snap distance)
                    if (distance < snapDistance * 0.5) {
                        refined.push(nearestBoundary);
                    } else if (distance < snapDistance) {
                        // Apply slight correction towards boundary
                        const correctionFactor = Math.min(0.3, distance / snapDistance);
                        refined.push({
                            lat: curr.lat + (nearestBoundary.lat - curr.lat) * correctionFactor,
                            lng: curr.lng + (nearestBoundary.lng - curr.lng) * correctionFactor
                        });
                    } else {
                        // Too far from boundary - keep original
                        refined.push(curr);
                    }
                } else {
                    // No nearby boundary - keep original
                    refined.push(curr);
                }
            }
        }
        
        return refined;
    }
    
    private cleanupDuplicatePoints(path: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
        const cleaned: google.maps.LatLngLiteral[] = [];
        const threshold = 0.00001; // ~1 meter in degrees
        
        for (let i = 0; i < path.length; i++) {
            const curr = path[i];
            const next = path[(i + 1) % path.length];
            
            const distance = Math.sqrt(
                Math.pow(curr.lat - next.lat, 2) + Math.pow(curr.lng - next.lng, 2)
            );
            
            // Only add if point is significantly different from next
            if (distance > threshold) {
                cleaned.push(curr);
            }
        }
        
        return cleaned.length > 2 ? cleaned : path; // Keep original if too many points removed
    }

    private densifyBoundaryNodes(path: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
        if (!this.nodeDensification || path.length < 2) {
            return path;
        }
        
        const densifiedPath: google.maps.LatLngLiteral[] = [path[0]]; // Start with first point
        
        for (let i = 0; i < path.length; i++) {
            const p1 = path[i];
            const p2 = path[(i + 1) % path.length]; // Wrap around for last edge
            
            // Check if this edge is close to any existing territory boundary
            const needsDensification = this.edgeNeedsDensification(p1, p2);
            
            if (needsDensification) {
                // Add 2-3 intermediate nodes along this edge
                const segments = 4; // Divide edge into 4 segments (3 intermediate points)
                for (let j = 1; j < segments; j++) {
                    const t = j / segments;
                    const intermediatePoint: google.maps.LatLngLiteral = {
                        lat: p1.lat + (p2.lat - p1.lat) * t,
                        lng: p1.lng + (p2.lng - p1.lng) * t
                    };
                    densifiedPath.push(intermediatePoint);
                }
            }
            
            // Always add the endpoint (unless it's the last edge where it's already the start)
            if (i < path.length - 1) {
                densifiedPath.push(p2);
            }
        }
        
        return densifiedPath;
    }
    
    private edgeNeedsDensification(p1: google.maps.LatLngLiteral, p2: google.maps.LatLngLiteral): boolean {
        if (this.existingTerritories.length === 0) {
            return false;
        }
        
        // Check if any point along this edge is close to an existing boundary
        // We'll sample the edge at multiple points
        const samples = 5;
        for (let i = 0; i <= samples; i++) {
            const t = i / samples;
            const samplePoint: google.maps.LatLngLiteral = {
                lat: p1.lat + (p2.lat - p1.lat) * t,
                lng: p1.lng + (p2.lng - p1.lng) * t
            };
            
            // Check if this sample point is close to any existing boundary
            for (const territory of this.existingTerritories) {
                if (this.isEditMode && this.editingTerritoryId && territory._id === this.editingTerritoryId) {
                    continue;
                }
                
                const nearestPoint = this.findClosestPointOnAnyEdge(samplePoint, territory.coordinates);
                if (nearestPoint) {
                    const distance = this.haversineDistance(samplePoint, nearestPoint);
                    if (distance < this.snapThreshold * 111320 * 1.5) { // 1.5x snap threshold
                        return true; // This edge is close to a boundary, needs densification
                    }
                }
            }
        }
        
        return false;
    }
    
    private findClosestPointOnAnyEdge(point: google.maps.LatLngLiteral, polygon: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral | null {
        if (!polygon || polygon.length < 2) {
            return null;
        }
        
        let closestPoint: google.maps.LatLngLiteral | null = null;
        let minDistance = Infinity;
        
        // Check all edges
        for (let i = 0; i < polygon.length; i++) {
            const edgeStart = polygon[i];
            const edgeEnd = polygon[(i + 1) % polygon.length];
            
            const closestOnEdge = this.closestPointOnEdge(point, edgeStart, edgeEnd);
            const distance = this.haversineDistance(point, closestOnEdge);
            
            if (distance < minDistance) {
                minDistance = distance;
                closestPoint = closestOnEdge;
            }
        }
        
        return closestPoint;
    }

    private findNearestSnapPoint(point: google.maps.LatLngLiteral): google.maps.LatLngLiteral | null {
        if (!this.snapToEdge || this.existingTerritories.length === 0) {
            return null;
        }

        const snapDistance = this.snapThreshold * 111320;
        const nodeConsolidationRadius = snapDistance * 0.3; // Check within 30% of snap distance

        // FIRST: Check for existing nodes nearby (within consolidation radius)
        const nearbyNode = this.findNearbyExistingNode(point, nodeConsolidationRadius);
        if (nearbyNode) {
            // Found an existing node - snap to it to consolidate
            return nearbyNode;
        }

        let closestPoint: google.maps.LatLngLiteral | null = null;
        let minDistanceMeters = snapDistance * 1.2; // 20% larger tolerance

        // SECOND: Prioritize vertices (corners/junctions) - these are important nodes
        for (const territory of this.existingTerritories) {
            // Skip the current territory being edited - don't snap to itself
            if (this.isEditMode && this.editingTerritoryId && territory._id === this.editingTerritoryId) {
                continue;
            }

            const coordinates = territory.coordinates;
            
            if (!coordinates || coordinates.length === 0) continue;
            
            // Check distance to all vertices first (corners are important)
            for (const vertex of coordinates) {
                const distance = this.haversineDistance(point, vertex);
                if (distance < minDistanceMeters) {
                    minDistanceMeters = distance;
                    closestPoint = vertex;
                }
            }
        }

        // THIRD: Check edges if no nearby vertex found
        if (closestPoint === null || minDistanceMeters > snapDistance * 0.5) {
            for (const territory of this.existingTerritories) {
                if (this.isEditMode && this.editingTerritoryId && territory._id === this.editingTerritoryId) {
                    continue;
                }

                const coordinates = territory.coordinates;
                
                if (!coordinates || coordinates.length === 0) continue;
                
                // Find the closest point on edges
            for (let i = 0; i < coordinates.length; i++) {
                const p1 = coordinates[i];
                const p2 = coordinates[(i + 1) % coordinates.length];
                
                if (!p1 || !p2) continue;
                
                const edgePoint = this.closestPointOnEdge(point, p1, p2);
                const distance = this.haversineDistance(point, edgePoint);
                
                    // Prefer edge points that are already close to vertices (junctions)
                    if (distance < minDistanceMeters) {
                    minDistanceMeters = distance;
                    closestPoint = edgePoint;
                    }
                }
            }
        }

        if (!closestPoint || minDistanceMeters > snapDistance) return null;

        // Don't snap to points outside the region bounds - this prevents
        // clampPointsToRegion from distorting the polygon and creating false overlaps
        if (!this.isInRegionBounds(closestPoint)) return null;

        // Prefer exact existing vertex when very close so both polygons share the same coordinates (no visible gap)
        const consolidated = this.findExistingVertexWithinDistance(closestPoint, this.vertexConsolidationMeters);
        return consolidated || closestPoint;
    }

    /** Return an existing territory vertex that is within distanceMeters of point, or null. Used to eliminate gaps by sharing exact coordinates. */
    private findExistingVertexWithinDistance(point: google.maps.LatLngLiteral, distanceMeters: number): google.maps.LatLngLiteral | null {
        for (const territory of this.existingTerritories) {
            if (this.isEditMode && this.editingTerritoryId && territory._id === this.editingTerritoryId) continue;
            const coordinates = territory.coordinates;
            if (!coordinates?.length) continue;
            for (const vertex of coordinates) {
                if (this.haversineDistance(point, vertex) <= distanceMeters) return vertex;
            }
        }
        return null;
    }
    
    private findNearbyExistingNode(point: google.maps.LatLngLiteral, radiusMeters: number): google.maps.LatLngLiteral | null {
        // Check all existing territory nodes to find one nearby
        for (const territory of this.existingTerritories) {
            if (this.isEditMode && this.editingTerritoryId && territory._id === this.editingTerritoryId) {
                continue;
            }

            const coordinates = territory.coordinates;
            
            if (!coordinates || coordinates.length === 0) continue;
            
            // Check each vertex of this territory
            for (const vertex of coordinates) {
                const distance = this.haversineDistance(point, vertex);
                if (distance <= radiusMeters) {
                    // Found a nearby existing node - return it
                    return vertex;
                }
            }
        }
        
        return null;
    }
    
    private checkIfNodeNeeded(point: google.maps.LatLngLiteral): boolean {
        // Check if there are multiple boundaries nearby that would benefit from a node here
        const boundariesNearby = this.countNearbyBoundaries(point);
        
        // If there are 2+ boundaries near this point, add a node for better alignment
        return boundariesNearby >= 2;
    }
    
    private countNearbyBoundaries(point: google.maps.LatLngLiteral): number {
        const radiusMeters = this.snapThreshold * 111320 * 0.5;
        let count = 0;
        
        for (const territory of this.existingTerritories) {
            if (this.isEditMode && this.editingTerritoryId && territory._id === this.editingTerritoryId) {
                continue;
            }

            const coordinates = territory.coordinates;
            if (!coordinates || coordinates.length === 0) continue;
            
            // Check if point is near any edge of this territory
            for (let i = 0; i < coordinates.length; i++) {
                const p1 = coordinates[i];
                const p2 = coordinates[(i + 1) % coordinates.length];
                
                if (!p1 || !p2) continue;
                
                const distance = this.pointToEdgeDistance(point, p1, p2);
                if (distance <= radiusMeters) {
                    count++;
                    break; // Count each territory only once
                }
            }
        }
        
        return count;
    }

    private closestPointOnEdge(point: google.maps.LatLngLiteral, edgeStart: google.maps.LatLngLiteral, edgeEnd: google.maps.LatLngLiteral): google.maps.LatLngLiteral {
        // Always use proper perpendicular projection to find closest point on line segment
        const x1 = edgeStart.lng;
        const y1 = edgeStart.lat;
        const x2 = edgeEnd.lng;
        const y2 = edgeEnd.lat;
        const x0 = point.lng;
        const y0 = point.lat;

        const dx = x2 - x1;
        const dy = y2 - y1;
        const lengthSquared = dx * dx + dy * dy;

        // Edge is a single point
        if (lengthSquared === 0) {
            return edgeStart;
        }

        // Calculate the projection parameter t
        // t represents how far along the edge segment the closest point is
        let t = ((x0 - x1) * dx + (y0 - y1) * dy) / lengthSquared;
        
        // Clamp t to [0, 1] to keep the point on the edge segment
        // This ensures we snap to the segment, not the infinite line
        t = Math.max(0, Math.min(1, t));

        // Calculate the closest point on the segment
        return {
            lng: x1 + t * dx,
            lat: y1 + t * dy
        };
    }

    private haversineDistance(p1: google.maps.LatLngLiteral, p2: google.maps.LatLngLiteral): number {
        // Use Google Maps geometry library for accurate distance calculation
        if (google.maps.geometry) {
            const point1 = new google.maps.LatLng(p1.lat, p1.lng);
            const point2 = new google.maps.LatLng(p2.lat, p2.lng);
            return google.maps.geometry.spherical.computeDistanceBetween(point1, point2); // Returns distance in meters
        }
        
        // Fallback to simple Euclidean distance in lat/lng space (degrees)
        const dx = p1.lng - p2.lng;
        const dy = p1.lat - p2.lat;
        return Math.sqrt(dx * dx + dy * dy);
    }

    toggleSnapToEdge(): void {
        this.snapToEdge = !this.snapToEdge;
        const message = this.snapToEdge 
            ? `Snapping enabled: Points will snap within ${this.snapDistanceMeters}m` 
            : 'Snapping disabled: Territories will be drawn exactly where you click';
        this.messageService.add({
            severity: this.snapToEdge ? 'success' : 'info',
            summary: this.snapToEdge ? 'Snap to Edge: ON' : 'Snap to Edge: OFF',
            detail: message,
            life: 3000
        });
    }

    onSnapThresholdChange(): void {
        // Convert meters to degrees (approximately 111,320 meters per degree)
        this.snapThreshold = this.snapDistanceMeters / 111320;
        
        // if (this.snapToEdge) {
        //     this.messageService.add({
        //         severity: 'info',
        //         summary: 'Snap Distance Updated',
        //         detail: `Snap threshold set to ${this.snapDistanceMeters} meters`,
        //         life: 2000
        //     });
        // }
    }

    private checkEditMode(): void {
        // Check if we're in edit mode from query parameters
        const urlParams = new URLSearchParams(window.location.search);
        const editMode = urlParams.get('edit');
        const territoryId = urlParams.get('id');
        const region = urlParams.get('region');
        
        if (editMode === 'true' && territoryId) {
            this.isEditMode = true;
            this.territoryForm.get('region')?.disable();
            this.editingTerritoryId = territoryId;
            this.mapLoading = true; // keep the map covered until the correct region is applied
            this.loadTerritoryForEditing(territoryId);
        }
        
        if (region) {
            const applied = this.applyRegionSnapshot(region);
            if (applied) {
                if (!this.isEditMode) {
                    this.shouldRenderMap = true;
                    this.mapLoading = false;
                } else {
                    this.shouldRenderMap = true;
                }
            }
        }
        
        // If region is provided and we're NOT in edit mode, set it as default
        if (region && editMode !== 'true') {
            // Wait for regions array to be loaded, then set the region
            this.setRegionWhenReady(region);
        }
    }

    private setRegionWhenReady(regionName: string): void {
        if (this.regions && this.regions.length > 0) {
            this.selectedRegionObject = this.regions.find(r => r.name === regionName);
            if (this.selectedRegionObject) {
                this.territoryForm.patchValue({ region: this.selectedRegionObject }); // Set the full region object, not just the name
                this.onRegionChange(regionName);
                console.log('Set default region from URL parameter:', regionName);
            }
        } else {
            // Regions not loaded yet, try again in 100ms
            setTimeout(() => this.setRegionWhenReady(regionName), 100);
        }
    }

    private loadSubscriberOperationalRegions(): void {
        // Get the selected subscriber ID from the subscription context service (used by interceptor)
        const selectedSubscriberId = this.subscriptionContextService.getSelectedSubscriberId();
        
        if (selectedSubscriberId) {
            console.log('Loading operational regions for selected subscriber:', selectedSubscriberId);
            this.subscriberService.findSubscriber(selectedSubscriberId).then((subscriber: any) => {
                // Check if subscriber is wrapped in a response object
                let actualSubscriber = subscriber;
                if (subscriber && subscriber.subscriber) {
                    actualSubscriber = subscriber.subscriber;
                }
                
                if (actualSubscriber?.operationalRegions && actualSubscriber.operationalRegions.length > 0) {
                    // Filter regions to only show those in subscriber's operational regions
                    this.regions = this.allRegions.filter(region => 
                        actualSubscriber.operationalRegions.includes(region.name)
                    );
                    console.log('Filtered regions based on subscriber operational regions:', this.regions);
                    console.log('Subscriber operational regions:', actualSubscriber.operationalRegions);
                } else {
                    // If no operational regions set, show all regions
                    this.regions = [...this.allRegions];
                    console.log('No operational regions found, showing all regions');
                }
            }).catch((error: any) => {
                console.error('Error loading subscriber operational regions:', error);
                // Fallback to showing all regions
                this.regions = [...this.allRegions];
            });
        } else {
            // If no selected subscriber ID, show all regions
            this.regions = [...this.allRegions];
            console.log('No selected subscriber ID found, showing all regions');
        }
    }

    private loadTerritoryForEditing(territoryId: string): void {
        this.loading = true;
        console.log('AddTerritoryComponent: Loading territory for editing:', territoryId);
        this.territoryService.getTerritoryById(territoryId).subscribe({
            next: (response) => {
                console.log('AddTerritoryComponent: Territory loaded successfully:', response);
                const territory = response.data;
                const regionName = territory.region;
                
                // Reset selection first, then ensure map centers on the territory's region immediately
                this.selectedRegionObject = null;
                if (regionName) {
                    const regionFromAllRegions = this.allRegions.find(r => r.name === regionName);
                    if (regionFromAllRegions) {
                        this.selectedRegionObject = regionFromAllRegions;
                        this.center = regionFromAllRegions.center;
                        this.zoom = regionFromAllRegions.zoom;

                        const bounds = regionFromAllRegions.bounds;
                        const latPadding = (bounds.north - bounds.south) * 0.1;
                        const lngPadding = (bounds.east - bounds.west) * 0.1;

                        this.options = {
                            ...this.options,
                            restriction: {
                                latLngBounds: {
                                    north: bounds.north + latPadding,
                                    south: bounds.south - latPadding,
                                    west: bounds.west - lngPadding,
                                    east: bounds.east + lngPadding
                                },
                                strictBounds: false
                            }
                        };
                    }
                }

                this.territoryForm.patchValue({
                    name: territory.name,
                    region: null,
                    isActive: territory.status === 'Active'
                });

                this.currentPolygon = [...territory.coordinates];

                if (regionName) {
                    // Ensure the region is applied once the regions list is populated
                    this.setRegionWhenReady(regionName);
                } else {
                    // If there is no region we can safely stop loading indicators here
                    this.loading = false;
                    this.mapLoading = false;
                    if (!this.shouldRenderMap) {
                        this.shouldRenderMap = true;
                    }
                }

                // Load assigned users for the territory
                if (territory.assignedUsersDetails && territory.assignedUsersDetails.length > 0) {
                    this.loadAssignedUsers(territory.assignedUsersDetails);
                } else {
                    this.selectedUsers = [];
                }

                // this.messageService.add({
                //     severity: 'info',
                //     summary: 'Edit Mode',
                //     detail: `Editing territory: ${territory.name}`
                // });
            },
            error: (error) => {
                console.error('AddTerritoryComponent: Error loading territory:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load territory for editing'
                });
                this.loading = false;
                this.mapLoading = false;
                if (!this.shouldRenderMap) {
                    this.shouldRenderMap = true;
                }
            }
        });
    }

    private setupFormListeners(): void {
        this.territoryForm.get('region')?.valueChanges.subscribe(region => {
            if (region) {
                // If region is an object, extract the name
                const regionName = typeof region === 'object' ? region.name : region;
                this.selectedRegionObject = this.regions.find(r => r.name === regionName);
                
                if (this.selectedRegionObject) {
                    this.onRegionChange(regionName);
                }
            }
        });
    }

    private checkDrawingLibraryAvailability(): void {
        // Check if Google Maps Drawing library is available
        const checkLibrary = () => {
            if ((window as any).google?.maps?.drawing) {
                console.log('Google Maps Drawing library is available');
                // this.messageService.add({
                //     severity: 'success',
                //     summary: 'Map Ready',
                //     detail: 'Drawing tools are now available. Use the map controls to draw territories.'
                // });
            } else {
                console.log('Google Maps Drawing library not yet available, retrying...');
                setTimeout(checkLibrary, 2000);
            }
        };
        
        // Start checking after a short delay
        setTimeout(checkLibrary, 1000);
    }

    private initializeMap(): void {
        if (this.mapRef?.googleMap) {
            this.setupDrawingManager();
            this.setupWheelZoomListener();
        } else {
            setTimeout(() => this.initializeMap(), 100);
        }
    }

    private setupDrawingManager(): void {
        // Wait for Google Maps and Drawing library to be fully loaded
        if (!(window as any).google?.maps?.drawing) {
            console.log('Waiting for Google Maps Drawing library...');
            setTimeout(() => this.setupDrawingManager(), 1000);
            return;
        }

        console.log('Setting up drawing manager...');
        
        try {
            this.drawingManager = new google.maps.drawing.DrawingManager({
                drawingMode: null,
                drawingControl: true,
                drawingControlOptions: {
                    position: google.maps.ControlPosition.TOP_CENTER,
                    drawingModes: [google.maps.drawing.OverlayType.POLYGON]
                },
                polygonOptions: {
                    editable: false,
                    draggable: false,
                    fillColor: '#FF0000',
                    fillOpacity: 0.3,
                    strokeColor: '#FF0000',
                    strokeWeight: 2
                }
            });

            this.drawingManager.setMap(this.mapRef.googleMap!);
            console.log('Drawing manager setup complete');
            
            // Force the drawing controls to be visible and ensure they're positioned correctly
            setTimeout(() => {
                if (this.drawingManager) {
                    this.drawingManager.setDrawingMode(null);
                    console.log('Drawing controls should now be visible');
                    
                    // Try to make drawing controls more prominent
                    const drawingControls = document.querySelector('.gm-style-moc');
                    if (drawingControls) {
                        (drawingControls as HTMLElement).style.zIndex = '1000';
                        (drawingControls as HTMLElement).style.position = 'relative';
                        console.log('Drawing controls found and styled');
                    } else {
                        console.log('Drawing controls not found in DOM');
                    }
                }
            }, 1000);
            
        } catch (error) {
            console.error('Error setting up drawing manager:', error);
        }

        // Add event listener for polygon completion
        if (this.drawingManager) {
            google.maps.event.addListener(this.drawingManager, 'overlaycomplete', (event: any) => {
                this.ngZone.run(() => {
                    if (event.type === 'polygon') {
                        let path = event.overlay.getPath().getArray().map((p: any) => ({
                            lat: p.lat(),
                            lng: p.lng()
                        }));
                        
                        // Save original unsnapped path for fallback if snapping causes overlap
                        const originalUnsnapPath = [...path];
                        
                        // Always apply snapping if enabled
                        if (this.snapToEdge) {
                            // Show processing message
                            this.messageService.add({
                                severity: 'info',
                                summary: 'Processing...',
                                detail: 'Applying intelligent boundary alignment...',
                                life: 2000
                            });
                            
                            path = this.applySnapping(path);
                            
                            // Show completion message with stats
                            const originalCount = event.overlay.getPath().getArray().length;
                            // this.messageService.add({
                            //     severity: 'success',
                            //     summary: 'Boundary Aligned',
                            //     detail: `Optimized polygon: ${originalCount} → ${path.length} points`,
                            //     life: 3000
                            // });
                        }
                        
                        // Clamp any points outside the country bounds to the nearest point on the boundary
                        const clampResult = this.clampPointsToRegion(path);
                        path = clampResult.path;
                        
                        // Notify user if some points were moved to the boundary
                        if (clampResult.adjustedCount > 0) {
                            this.messageService.add({
                                severity: 'warn',
                                summary: 'Points Adjusted',
                                detail: `${clampResult.adjustedCount} point(s) outside the country boundary were moved to the nearest border.`,
                                life: 3000
                            });
                        }
                        
                        // Then validate the polygon against existing territories
                        let isValid = this.isAreaAvailableForNewPolygon(path);
                        
                        if (!isValid) {
                            // Check if the overlap is just shared boundaries from snapping
                            const hasSharedBoundaries = this.checkIfOnlySharedBoundaries(path);
                            
                            if (!hasSharedBoundaries) {
                                // Overlap after snapping - try fallback to original unsnapped path
                                if (this.snapToEdge) {
                                    const fallbackClamp = this.clampPointsToRegion(originalUnsnapPath);
                                    const fallbackPath = fallbackClamp.path;
                                    
                                    if (this.isAreaAvailableForNewPolygon(fallbackPath)) {
                                        // Original path is fine - snapping caused the false overlap
                                        path = fallbackPath;
                                        isValid = true;
                                        console.log('Snapping caused overlap near country border - using original path as fallback');
                                        this.messageService.add({
                                            severity: 'warn',
                                            summary: 'Snapping Adjusted',
                                            detail: 'Boundary snapping was reduced to avoid overlap near the country border.',
                                            life: 3000
                                        });
                                    } else {
                                        // Original path also has overlap - genuine overlap
                                        this.messageService.add({
                                            severity: 'error',
                                            summary: 'Territory Overlap',
                                            detail: 'This area overlaps with existing territories. Please adjust the boundaries.'
                                        });
                                        event.overlay.setMap(null);
                                        return;
                                    }
                                } else {
                                    // No snapping was applied - genuine overlap
                                    this.messageService.add({
                                        severity: 'error',
                                        summary: 'Territory Overlap',
                                        detail: 'This area overlaps with existing territories. Please adjust the boundaries.'
                                    });
                                    event.overlay.setMap(null);
                                    return;
                                }
                            }
                            // Shared boundary only - allow it
                        }
                        
                        this.currentPolygon = path;
                        this.isDrawingMode = false;
                        this.drawingManager?.setDrawingMode(null);
                        
                        // Remove the temporary overlay
                        event.overlay.setMap(null);
                        
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Polygon Ready',
                            detail: 'Territory polygon optimized. Fill in details and save.'
                        });
                    }
                });
            });
        }

        // Add event listener for drawing mode changes - setup snapping
        if (this.drawingManager) {
            google.maps.event.addListener(this.drawingManager, 'drawingmode_changed', () => {
                this.ngZone.run(() => {
                    const drawingMode = this.drawingManager?.getDrawingMode();
                    if (drawingMode === google.maps.drawing.OverlayType.POLYGON) {
                        this.isDrawingMode = true;
                        // this.messageService.add({
                        //     severity: 'info',
                        //     summary: 'Drawing Mode Active',
                        //     detail: 'Click on the map to create polygon points. Double-click to complete the polygon.'
                        // });
                        // Setup mouse tracking for snapping during drawing
                        this.setupDrawingSnapping();
                    } else {
                        this.isDrawingMode = false;
                        this.removeDrawingSnapping();
                    }
                });
            });
        }
    }

    private drawingSnapListeners: google.maps.MapsEventListener[] = [];

    private setupDrawingSnapping(): void {
        if (!this.mapRef?.googleMap || !this.snapToEdge) return;

        // Listen for clicks during drawing to implement snapping
        const clickListener = google.maps.event.addListener(
            this.mapRef.googleMap,
            'click',
            (event: google.maps.MapMouseEvent) => {
                if (!this.isDrawingMode || !event.latLng) return;

                // This is a workaround - we can't directly control the Google Maps drawing
                // But we can at least provide feedback. The actual snapping happens post-completion
                if (this.snapToEdge) {
                    const point: google.maps.LatLngLiteral = {
                        lat: event.latLng.lat(),
                        lng: event.latLng.lng()
                    };
                    const snapped = this.findNearestSnapPoint(point);
                    if (snapped) {
                        console.log('Snap point available:', snapped);
                        // Visual feedback via console - actual snapping happens in applySnapping
                    }
                }
            }
        );

        this.drawingSnapListeners.push(clickListener);
    }

    private removeDrawingSnapping(): void {
        this.drawingSnapListeners.forEach(listener => listener.remove());
        this.drawingSnapListeners = [];
    }

    private onRegionChange(region: string): void {
        this.mapLoading = true;

        // Find the region from the regions array and set it as selected
        const selectedRegion = this.regions.find(r => r.name === region);
        if (selectedRegion) {
            this.selectedRegionObject = selectedRegion;
            this.center = selectedRegion.center;
            this.zoom = selectedRegion.zoom;
            
            // Update map restriction
            const bounds = selectedRegion.bounds;
            const latPadding = (bounds.north - bounds.south) * 0.1;
            const lngPadding = (bounds.east - bounds.west) * 0.1;
            
            this.options = {
                ...this.options,
                restriction: {
                    latLngBounds: {
                        north: bounds.north + latPadding,
                        south: bounds.south - latPadding,
                        west: bounds.west - lngPadding,
                        east: bounds.east + lngPadding
                    },
                    strictBounds: false
                }
            };
        } else {
            this.mapLoading = false;
            return;
        }
        
        // Load existing territories for this region
        this.loadExistingTerritories(region);
    }

    private applyRegionSnapshot(regionName: string): boolean {
        const regionConfig = this.allRegions.find(r => r.name === regionName);
        if (!regionConfig) {
            return false;
        }

        this.selectedRegionObject = regionConfig;
        this.center = regionConfig.center;
        this.zoom = regionConfig.zoom;

        const bounds = regionConfig.bounds;
        const latPadding = (bounds.north - bounds.south) * 0.1;
        const lngPadding = (bounds.east - bounds.west) * 0.1;

        this.options = {
            ...this.options,
            restriction: {
                latLngBounds: {
                    north: bounds.north + latPadding,
                    south: bounds.south - latPadding,
                    west: bounds.west - lngPadding,
                    east: bounds.east + lngPadding
                },
                strictBounds: false
            }
        };

        return true;
    }

    private loadExistingTerritories(region: string): void {
        this.loading = true;
        this.mapLoading = true;
        this.territoryService.getTerritoriesByRegion(region).subscribe({
            next: (response) => {
                // Filter out the current territory being edited from the list
                if (this.isEditMode && this.editingTerritoryId) {
                    this.existingTerritories = response.data.filter(
                        t => t._id !== this.editingTerritoryId
                    );
                    console.log(`Loaded ${this.existingTerritories.length} existing territories (excluding current territory being edited)`);
                } else {
                this.existingTerritories = response.data;
                    console.log(`Loaded ${this.existingTerritories.length} existing territories`);
                }
                this.loading = false;
                if (!this.shouldRenderMap) {
                    this.shouldRenderMap = true;
                }
                setTimeout(() => {
                    this.mapLoading = false;
                    // Draw country border after map is ready
                    this.drawCountryBorder();
                    
                    // If in edit mode, zoom to the territory's polygon after map is ready
                    if (this.isEditMode && this.currentPolygon.length > 0) {
                        this.zoomToTerritoryPolygon();
                    }
                }, 200);
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load existing territories'
                });
                this.loading = false;
                this.mapLoading = false;
                if (!this.shouldRenderMap) {
                    this.shouldRenderMap = true;
                }
                // Draw country border even on error
                setTimeout(() => {
                    this.drawCountryBorder();
                }, 200);
            }
        });
    }

    // Set subscription context to user's actual subscription
    private setUserSubscriptionContext(): void {
        const currentUser = this.authService.getLoggedInUser();
        if (currentUser?.subscriberId) {
            // Extract the ID string from subscriberId object (which can be an object with _id property)
            const subscriberIdString = typeof currentUser.subscriberId === 'object' 
                ? currentUser.subscriberId._id 
                : currentUser.subscriberId;
            const companyName = typeof currentUser.subscriberId === 'object' 
                ? currentUser.subscriberId.companyName 
                : undefined;
            console.log('Setting subscription context to user\'s actual subscription:', subscriberIdString);
            this.subscriptionContextService.setSelectedSubscriber(subscriberIdString, companyName);
        }
    }

    // Clear subscription context to avoid interceptor conflicts
    private clearSubscriptionContext(): void {
        console.log('Clearing subscription context to avoid interceptor conflicts');
        this.subscriptionContextService.clearSelectedSubscriber();
    }

    private loadAvailableUsers(): void {
        const currentUser = this.authService.getLoggedInUser();
        
        if (!currentUser) {
            console.error('No logged in user found');
            this.messageService.add({
                severity: 'error',
                summary: 'Authentication Error',
                detail: 'Please log in again to continue'
            });
            return;
        }
          
        // Use getAllUsers which will be filtered by the interceptor
        this.userService.getPagedUsers({
            pageIndex: 0,
            pageSize: 1000,
            filters: {
                subscriberId: this.selectedSubscriberId || undefined,
                status: 'APPROVED'
            }
        }).subscribe({
            next: (response) => {
                if (response.data && response.data.length > 0) {
                    // Filter only approved, active, non-archived users with FSR role
                    this.availableUsers = response.data.filter(user => 
                        user.status === 'APPROVED' && 
                        user.roleId?.identification === 'FSR' &&
                        user.isActive === true &&
                        user.isArchived === false
                    );
                    console.log('Available FSR users loaded:', this.availableUsers.length);
                    
                    if (this.availableUsers.length === 0) {
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'No FSRs Available',
                            detail: 'No approved FSR users found for this subscription'
                        });
                    } else {
                        // In add mode, automatically assign all available FSR users
                        if (!this.isEditMode) {
                            this.selectedUsers = [...this.availableUsers];
                            console.log('Automatically assigned all available FSR users in add mode:', this.selectedUsers.length);
                        }
                    }
                } else {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'No Users Found',
                        detail: 'No users found for this subscription'
                    });
                }
            },
            error: (error) => {
                console.error('Error loading available users:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load available users. Please try again.'
                });
            }
        });
    }

    /**
     * Load and mark assigned users for editing a territory
     * @param assignedUsers Array of user detail objects that are already assigned to the territory
     */
    private loadAssignedUsers(assignedUsers: any[]): void {
        if (!assignedUsers || assignedUsers.length === 0) {
            this.selectedUsers = [];
            return;
        }

        // Wait for available users to be loaded first
        if (this.availableUsers.length === 0) {
            console.log('Available users not loaded yet, waiting for them');
            // If available users haven't been loaded yet, wait for them
            setTimeout(() => this.loadAssignedUsers(assignedUsers), 100);
            return;
        }

        // Extract user IDs from the assigned users array
        const assignedUserIds = assignedUsers.map(user => user._id);
        
        // Find the assigned users from the available users list
        this.selectedUsers = this.availableUsers.filter(user => 
            assignedUserIds.includes(user._id)
        );

        console.log(`Loaded ${this.selectedUsers.length} assigned users out of ${assignedUserIds.length} assigned IDs`);
        
        // Show a message if some assigned users couldn't be found
        if (this.selectedUsers.length !== assignedUserIds.length) {
            const missingCount = assignedUserIds.length - this.selectedUsers.length;
            // this.messageService.add({
            //     severity: 'warn',
            //     summary: 'Some Users Not Found',
            //     detail: `${missingCount} assigned users are no longer available in the system`
            // });
        }
    }

    startDrawing(): void {
        this.isDrawingMode = true;
        this.drawingManager?.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
        // this.messageService.add({
        //     severity: 'info',
        //     summary: 'Drawing Mode',
        //     detail: 'Click and drag to draw the territory polygon'
        // });
    }

    cancelDrawing(): void {
        this.isDrawingMode = false;
        this.currentPolygon = [];
        this.drawingManager?.setDrawingMode(null);
    }

    clearPolygon(): void {
        this.currentPolygon = [];
        this.isDrawingMode = false;
        this.isPolygonEditingMode = false;
        this.drawingManager?.setDrawingMode(null);
        this.cleanupEditingOverlay();
        this.messageService.add({
            severity: 'info',
            summary: 'Polygon Cleared',
            detail: 'Territory polygon has been cleared. You can draw a new one.'
        });
    }

    resetForm(): void {
        // Reset the form
        this.territoryForm.reset({
            name: '',
            region: '',
            isActive: true
        });
        
        // Clear the polygon
        this.currentPolygon = [];
        this.isDrawingMode = false;
        this.drawingManager?.setDrawingMode(null);
        
        // Reset user selection to all available users
        this.selectedUsers = [...this.availableUsers];
        this.searchQuery = '';
        
        // Show success message
        this.messageService.add({
            severity: 'success',
            summary: 'Form Reset',
            detail: 'Form has been reset. You can now create another territory or edit the current one.'
        });
    }

    editCurrentTerritory(): void {
        // Enable editing mode for the current polygon
        this.isEditMode = true;
        // this.messageService.add({
        //     severity: 'info',
        //     summary: 'Edit Mode',
        //     detail: 'You can now edit the territory polygon. Use the map controls to modify the shape.'
        // });
    }

    onSearchChange(): void {
        if (this.searchQuery.trim()) {
            const query = this.searchQuery.toLowerCase();
            // Filter from available users, not selected users
            const filteredUsers = this.availableUsers.filter(user => 
                user.firstName.toLowerCase().includes(query) ||
                user.lastName.toLowerCase().includes(query) ||
                user.email.toLowerCase().includes(query)
            );
            // Show filtered users that are already selected
            this.selectedUsers = this.selectedUsers.filter(user => 
                filteredUsers.some(fu => fu._id === user._id)
            );
        } else {
            // Reset to show all selected users
            this.selectedUsers = [...this.selectedUsers];
        }
    }

    addUser(user: User): void {
        if (!user || !user._id) {
            console.error('Invalid user object provided');
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Invalid user data provided'
            });
            return;
        }
        
        if (this.selectedUsers.find(u => u._id === user._id)) {
            this.messageService.add({
                severity: 'warn',
                summary: 'User Already Added',
                detail: `${user.firstName} ${user.lastName} is already assigned to this territory`
            });
            return;
        }
        
        this.selectedUsers.push(user);
        console.log('User added to territory:', user);
        this.messageService.add({
            severity: 'success',
            summary: 'User Added',
            detail: `${user.firstName} ${user.lastName} added to territory`
        });
    }

    removeUser(userId: string): void {
        if (!userId) {
            console.error('Invalid user ID provided');
            return;
        }
        
        const user = this.selectedUsers.find(u => u._id === userId);
        if (user) {
            this.selectedUsers = this.selectedUsers.filter(u => u._id !== userId);
            console.log('User removed from territory:', user);
            this.messageService.add({
                severity: 'success',
                summary: 'User Removed',
                detail: `${user.firstName} ${user.lastName} removed from territory`
            });
        } else {
            console.warn('User not found in selected users:', userId);
        }
    }

    isUserSelected(userId: string): boolean {
        return this.selectedUsers.some(user => user._id === userId);
    }

    getAvailableUsersForAdding(): User[] {
        return this.availableUsers.filter(u => !this.selectedUsers.find(su => su._id === u._id));
    }

    /**
     * Fetch existing products for the current subscription and add the new territory ID to their availableTerritories
     * @param territoryId The ID of the newly created territory
     */
    private addTerritoryToExistingProducts(territoryId: string): void {
        if (!this.selectedSubscriberId) {
            console.log('No subscriber ID available, skipping product updates');
            return;
        }

        console.log('Adding territory to existing products for subscriber:', this.selectedSubscriberId);
        
        // Fetch all active products for the current subscription
        this.productService.getProductsBySubscriberId(this.selectedSubscriberId, true, false).subscribe({
            next: (products: Product[]) => {
                if (products.length === 0) {
                    console.log('No existing products found for this subscription');
                    return;
                }

                console.log(`Found ${products.length} existing products to update`);
                
                // Update each product to include the new territory
                const updateObservables = products.map(product => {
                    const currentTerritoryIds = product.availableTerritories.map(t => t._id);
                    
                    // Only update if the territory is not already in the list
                    if (!currentTerritoryIds.includes(territoryId)) {
                        const updatedTerritoryIds = [...currentTerritoryIds, territoryId];
                        
                        return this.productService.updateProduct(product._id, {
                            availableTerritories: updatedTerritoryIds
                        }).pipe(
                            catchError(error => {
                                console.error(`Error updating product ${product._id}:`, error);
                                return of(null); // Return null for failed updates
                            })
                        );
                    }
                    return of(product); // Return original product if no update needed
                });

                // Wait for all updates to complete
                forkJoin(updateObservables).subscribe({
                    next: (results) => {
                        const successfulUpdates = results.filter(result => result !== null).length;
                        console.log(`Successfully updated ${successfulUpdates} products with new territory`);
                        
                        if (successfulUpdates > 0) {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Products Updated',
                                detail: `Added territory to ${successfulUpdates} existing products`
                            });
                        }
                    },
                    error: (error) => {
                        console.error('Error updating products with new territory:', error);
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Product Update Warning',
                            detail: 'Territory created but some products may not have been updated'
                        });
                    }
                });
            },
            error: (error) => {
                console.error('Error fetching existing products:', error);
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Product Update Warning',
                    detail: 'Territory created but could not update existing products'
                });
            }
        });
    }

    saveTerritory(): void {
        // Prevent saving while polygon editing is active
        if (this.isPolygonEditingMode) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Cannot Save',
                detail: 'Please click "Stop Editing" to finish editing the polygon before saving.',
                life: 4000
            });
            return;
        }

        if (this.territoryForm.invalid) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please fill in all required fields'
            });
            return;
        }

        if (this.currentPolygon.length < 3) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please draw a territory polygon with at least 3 points'
            });
            return;
        }

        if (this.selectedUsers.length === 0) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please assign at least one FSR to the territory'
            });
            return;
        }

        // Validate user data
        const invalidUsers = this.selectedUsers.filter(user => !user._id || !user.firstName || !user.lastName);
        if (invalidUsers.length > 0) {
            console.error('Invalid user data found:', invalidUsers);
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Some assigned users have invalid data. Please refresh and try again.'
            });
            return;
        }

        // Strict validation before saving - ensure no overlaps
        // if (!this.isAreaAvailableForNewPolygon(this.currentPolygon)) {
        //     this.messageService.add({
        //         severity: 'error',
        //         summary: 'Validation Error',
        //         detail: 'Territory overlaps with existing territories. Please adjust the boundaries before saving.'
        //     });
        //     return;
        // }

        console.log('Saving territory with users:', {
            userCount: this.selectedUsers.length,
            users: this.selectedUsers.map(u => ({ id: u._id, name: `${u.firstName} ${u.lastName}` }))
        });

        this.loading = true;

        if (this.isEditMode && this.editingTerritoryId) {
            // Update existing territory
            const updateData = {
                name: this.territoryForm.value.name,
                region: this.territoryForm.value.region?.name ,
                coordinates: this.currentPolygon,
                status: (this.territoryForm.value.isActive ? 'Active' : 'Inactive') as 'Active' | 'Inactive'
            };

            console.log('AddTerritoryComponent: Updating territory with data:', updateData);
            this.territoryService.updateTerritory(this.editingTerritoryId, updateData).subscribe({
                next: (response) => {
                    console.log('AddTerritoryComponent: Territory updated successfully:', response);
                    // Assign users to the territory
                    console.log('AddTerritoryComponent: Assigning users to territory:', this.selectedUsers.map(user => user._id));
                    this.territoryService.assignUsersToTerritory(this.editingTerritoryId!, {
                        territoryId: this.editingTerritoryId!,
                        userIds: this.selectedUsers.map(user => user._id)
                    }).subscribe({
                        next: () => {
                            console.log('AddTerritoryComponent: Users assigned successfully');
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Success',
                                detail: 'Territory updated and users assigned successfully'
                            });
                            
                            // Navigate back to territory management
                            this.router.navigate(['/admin/territory-management']);
                            this.loading = false;
                        },
                        error: (error) => {
                            console.error('AddTerritoryComponent: Error assigning users:', error);
                            this.messageService.add({
                                severity: 'warn',
                                summary: 'Warning',
                                detail: 'Territory updated but failed to assign users. You can assign them later.'
                            });
                            
                            // Navigate back to territory management
                            this.router.navigate(['/admin/territory-management']);
                            this.loading = false;
                        }
                    });
                },
                error: (error) => {
                    console.error('AddTerritoryComponent: Error updating territory:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update territory: ' + (error.error?.message || error.message || 'Unknown error')
                    });
                    this.loading = false;
                }
            });
        } else {
            // Create new territory
            // if (!this.selectedSubscriberId) {
            //     this.messageService.add({
            //         severity: 'error',
            //         summary: 'Error',
            //         detail: 'No subscription context selected. Please select a subscription first.'
            //     });
            //     this.loading = false;
            //     return;
            // }

            const territoryData: CreateTerritoryRequest = {
                name: this.territoryForm.value.name,
                region: this.territoryForm.value.region?.name ,
                coordinates: this.currentPolygon,
                status: this.territoryForm.value.isActive ? 'Active' : 'Inactive', 
            };

            this.territoryService.createTerritory(territoryData).subscribe({
                next: (response) => {
                    // Assign users to the territory
                    const territoryId = response.data._id;
                    this.territoryService.assignUsersToTerritory(territoryId, {
                        territoryId,
                        userIds: this.selectedUsers.map(user => user._id)
                    }).subscribe({
                        next: () => {
                            // Add the new territory to existing products in the subscription
                            this.addTerritoryToExistingProducts(territoryId);
                            
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Success',
                                detail: 'Territory created and users assigned successfully'
                            });
                            
                            // Reset form and stay in same window for editing
                            // this.resetForm();
                            this.router.navigate(['/admin/territory-management']);
                            this.loading = false;
                        },
                        error: (error) => {
                            console.error('Error assigning users:', error);
                            
                            // Still add territory to products even if user assignment fails
                            this.addTerritoryToExistingProducts(territoryId);
                            
                            this.messageService.add({
                                severity: 'warn',
                                summary: 'Warning',
                                detail: 'Territory created but failed to assign users. You can assign them later.'
                            });
                            
                            // Reset form and stay in same window
                            this.resetForm();
                            this.loading = false;
                        }
                    });
                },
                error: (error) => {
                    console.error('Error creating territory:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create territory: ' + (error.error?.message || error.message || 'Unknown error')
                    });
                    this.loading = false;
                }
            });
        }
    }

    cancel(): void {
        this.router.navigate(['/admin/territory-management']);
    }

    // Map validation methods from test-map
    private isAreaAvailableForNewPolygon(path: google.maps.LatLngLiteral[]): boolean {
        return !this.checkPolygonOverlap(path) && this.isPolygonInRegion(path);
    }

    private checkIfOnlySharedBoundaries(newPath: google.maps.LatLngLiteral[]): boolean {
        // Check if this is just shared boundaries (adjacent territories) vs actual overlap
        
        // First, check how many points are ON boundaries (shared vertices/edges)
        let pointsOnBoundary = 0;
        let pointsInside = 0;
        const totalPoints = newPath.length;
        
        for (const point of newPath) {
            let isOnBoundary = false;
            let isInside = false;
            
            for (const territory of this.existingTerritories) {
                if (this.isEditMode && this.editingTerritoryId && territory._id === this.editingTerritoryId) {
                    continue;
                }
                
                if (this.isPointOnBoundary(point, territory.coordinates)) {
                    pointsOnBoundary++;
                    isOnBoundary = true;
                }
                
                if (!isOnBoundary && this.isPointInsidePolygon(point, territory.coordinates)) {
                    pointsInside++;
                    isInside = true;
                }
            }
        }
        
        // STRICT: Allow only if:
        // - At least 50% of points are on boundaries (significant shared boundary)
        // - AND less than 5% are truly inside (minimal overlap)
        const boundaryRatio = pointsOnBoundary / totalPoints;
        const interiorRatio = pointsInside / totalPoints;
        
        console.log(`Shared boundary check: ${boundaryRatio.toFixed(2)} on boundary, ${interiorRatio.toFixed(2)} inside`);
        
        return boundaryRatio >= 0.5 && interiorRatio < 0.05;
    }

    private checkPolygonOverlap(newPath: google.maps.LatLngLiteral[]): boolean {
        for (const existingTerritory of this.existingTerritories) {
            // Skip the current territory being edited to avoid self-overlap detection
            if (this.isEditMode && this.editingTerritoryId && existingTerritory._id === this.editingTerritoryId) {
                continue;
            }
            
            // Check if this is just adjacent territories (shared boundaries allowed)
            const isAdjacent = this.areTerritoriesAdjacent(newPath, existingTerritory.coordinates);
            if (isAdjacent) {
                console.log('Territories are adjacent (shared boundary) - no overlap:', existingTerritory.name || existingTerritory._id);
                continue; // Allow adjacent territories
            }
            
            const hasOverlap = this.polygonsIntersect(newPath, existingTerritory.coordinates);
            if (hasOverlap) {
                console.log('Overlap detected with territory:', existingTerritory.name || existingTerritory._id);
                return true;
            }
        }
        
        return false;
    }
    
    private areTerritoriesAdjacent(path1: google.maps.LatLngLiteral[], path2: google.maps.LatLngLiteral[]): boolean {
        // Count how many points from path1 are on or very close to path2's boundaries
        let boundaryMatches = 0;
        let interiorPoints = 0;
        
        for (const point of path1) {
            const isOnBoundary = this.isPointOnBoundary(point, path2);
            if (isOnBoundary) {
                boundaryMatches++;
            } else if (this.isPointInsidePolygon(point, path2)) {
                interiorPoints++;
            }
        }
        
        const totalPoints = path1.length;
        const boundaryRatio = boundaryMatches / totalPoints;
        const interiorRatio = interiorPoints / totalPoints;
        
        // STRICT: Territories are adjacent ONLY if:
        // - At least 40% of points are on boundaries (significant shared boundary)
        // - AND less than 10% are truly interior (minimal accidental overlap)
        // - AND boundary points significantly outnumber interior points (at least 4:1 ratio)
        
        const hasSignificantSharedBoundary = boundaryRatio >= 0.4;
        const hasMinimalInterior = interiorRatio < 0.1;
        const boundariesDominant = boundaryMatches >= (interiorPoints * 4);
        
        const isAdjacent = hasSignificantSharedBoundary && hasMinimalInterior && boundariesDominant;
        
        if (isAdjacent) {
            console.log(`Adjacent territories detected: ${boundaryRatio.toFixed(2)} boundary ratio, ${interiorRatio.toFixed(2)} interior ratio`);
        }
        
        return isAdjacent;
    }

    private polygonsIntersect(path1: google.maps.LatLngLiteral[], path2: google.maps.LatLngLiteral[]): boolean {
        // First check: Count points that are on boundaries vs truly inside
        let pointsOnBoundary = 0;
        let pointsInside = 0;
        
        for (const point of path1) {
            if (this.isPointOnBoundary(point, path2)) {
                pointsOnBoundary++;
            } else {
                // Check if truly inside (not on boundary)
                if (this.isPointInsidePolygon(point, path2)) {
                    pointsInside++;
                }
            }
        }
        
        // STRICT: If more than 5% of points are truly inside, it's likely an overlap
        const interiorRatio = pointsInside / path1.length;
        
        if (interiorRatio > 0.05) {
            console.log(`Overlap detected: ${interiorRatio.toFixed(2)} of points are inside`);
            return true;
        }
        
        // Check reverse - if more than 5% of existing territory's points are inside new polygon
        pointsOnBoundary = 0;
        pointsInside = 0;
        for (const point of path2) {
            if (this.isPointOnBoundary(point, path1)) {
                pointsOnBoundary++;
            } else {
                // Check if truly inside (not on boundary)
                if (this.isPointInsidePolygon(point, path1)) {
                    pointsInside++;
                }
            }
        }
        
        const reverseInteriorRatio = pointsInside / path2.length;
        
        if (reverseInteriorRatio > 0.05) {
            console.log(`Reverse overlap detected: ${reverseInteriorRatio.toFixed(2)} of existing territory is inside`);
            return true;
        }
        
        // Check for crossing edges (not shared boundaries)
        for (let i = 0; i < path1.length; i++) {
            const edge1Start = path1[i];
            const edge1End = path1[(i + 1) % path1.length];
            
            for (let j = 0; j < path2.length; j++) {
                const edge2Start = path2[j];
                const edge2End = path2[(j + 1) % path2.length];
                
                // Skip if edges are identical (shared boundary)
                if (this.areEdgesIdentical(edge1Start, edge1End, edge2Start, edge2End)) {
                    continue;
                }
                
                if (this.edgesIntersect(edge1Start, edge1End, edge2Start, edge2End)) {
                    return true;
                }
            }
        }
        
        return false;
    }

    private areEdgesIdentical(
        edge1Start: google.maps.LatLngLiteral,
        edge1End: google.maps.LatLngLiteral,
        edge2Start: google.maps.LatLngLiteral,
        edge2End: google.maps.LatLngLiteral
    ): boolean {
        const tolerance = 1e-6; // Very small tolerance for floating point comparison
        
        // Check if edges are identical (same direction)
        const isMatch1 = Math.abs(edge1Start.lat - edge2Start.lat) < tolerance &&
                        Math.abs(edge1Start.lng - edge2Start.lng) < tolerance &&
                        Math.abs(edge1End.lat - edge2End.lat) < tolerance &&
                        Math.abs(edge1End.lng - edge2End.lng) < tolerance;
        
        // Check if edges are identical (reverse direction)
        const isMatch2 = Math.abs(edge1Start.lat - edge2End.lat) < tolerance &&
                        Math.abs(edge1Start.lng - edge2End.lng) < tolerance &&
                        Math.abs(edge1End.lat - edge2Start.lat) < tolerance &&
                        Math.abs(edge1End.lng - edge2Start.lng) < tolerance;
        
        return isMatch1 || isMatch2;
    }

    private isPointInsidePolygon(point: google.maps.LatLngLiteral, polygon: google.maps.LatLngLiteral[]): boolean {
        // First check if point is on the boundary - if so, it's not considered an overlap
        const toleranceMeters = 30; // Moderate tolerance for boundary detection to allow proper snapping
        
        // Check if point matches any vertex
        for (const vertex of polygon) {
            const distance = this.haversineDistance(point, vertex);
            if (distance < toleranceMeters) {
                return false; // On vertex - not inside
            }
        }
        
        // Check if point is on any edge
        for (let i = 0; i < polygon.length; i++) {
            const edgeStart = polygon[i];
            const edgeEnd = polygon[(i + 1) % polygon.length];
            const distance = this.pointToEdgeDistance(point, edgeStart, edgeEnd);
            if (distance < toleranceMeters) {
                return false; // On edge - not inside
            }
        }
        
        // Then check if point is actually inside
        return this.isPointInPolygon(point, polygon);
    }

    private isPointOnBoundary(point: google.maps.LatLngLiteral, polygon: google.maps.LatLngLiteral[]): boolean {
        // Stricter tolerance for better overlap detection
        const toleranceMeters = 20; // 20 meters tolerance - more precise for overlap detection
        
        // First check if point matches any vertex exactly
        for (const vertex of polygon) {
            const distance = this.haversineDistance(point, vertex);
            if (distance < toleranceMeters) {
                return true;
            }
        }
        
        // Check if point is on any edge
        for (let i = 0; i < polygon.length; i++) {
            const edgeStart = polygon[i];
            const edgeEnd = polygon[(i + 1) % polygon.length];
            
            const distance = this.pointToEdgeDistance(point, edgeStart, edgeEnd);
            if (distance < toleranceMeters) {
                return true;
            }
        }
        
        return false;
    }

    private pointToEdgeDistance(point: google.maps.LatLngLiteral, edgeStart: google.maps.LatLngLiteral, edgeEnd: google.maps.LatLngLiteral): number {
        // Calculate perpendicular distance from point to edge using meters
        if (google.maps.geometry) {
            const clickedPoint = new google.maps.LatLng(point.lat, point.lng);
            const startPoint = new google.maps.LatLng(edgeStart.lat, edgeStart.lng);
            const endPoint = new google.maps.LatLng(edgeEnd.lat, edgeEnd.lng);
            
            // Find closest point on edge
            const dx = edgeEnd.lng - edgeStart.lng;
            const dy = edgeEnd.lat - edgeStart.lat;
            const lengthSquared = dx * dx + dy * dy;
            
            if (lengthSquared === 0) {
                // Edge is a point
                return google.maps.geometry.spherical.computeDistanceBetween(clickedPoint, startPoint);
            }
            
            const t = Math.max(0, Math.min(1, ((point.lng - edgeStart.lng) * dx + (point.lat - edgeStart.lat) * dy) / lengthSquared));
            const projection: google.maps.LatLngLiteral = {
                lng: edgeStart.lng + t * dx,
                lat: edgeStart.lat + t * dy
            };
            
            const projectionPoint = new google.maps.LatLng(projection.lat, projection.lng);
            return google.maps.geometry.spherical.computeDistanceBetween(clickedPoint, projectionPoint);
        }
        
        // Fallback: simple degree-based calculation
        const dx = edgeEnd.lng - edgeStart.lng;
        const dy = edgeEnd.lat - edgeStart.lat;
        const lengthSquared = dx * dx + dy * dy;
        
        if (lengthSquared === 0) {
            return this.haversineDistance(point, edgeStart);
        }
        
        const t = Math.max(0, Math.min(1, ((point.lng - edgeStart.lng) * dx + (point.lat - edgeStart.lat) * dy) / lengthSquared));
        const projection: google.maps.LatLngLiteral = {
            lng: edgeStart.lng + t * dx,
            lat: edgeStart.lat + t * dy
        };
        
        return this.haversineDistance(point, projection);
    }

    private edgesIntersect(
        edge1Start: google.maps.LatLngLiteral, 
        edge1End: google.maps.LatLngLiteral,
        edge2Start: google.maps.LatLngLiteral, 
        edge2End: google.maps.LatLngLiteral
    ): boolean {
        const x1 = edge1Start.lng;
        const y1 = edge1Start.lat;
        const x2 = edge1End.lng;
        const y2 = edge1End.lat;
        const x3 = edge2Start.lng;
        const y3 = edge2Start.lat;
        const x4 = edge2End.lng;
        const y4 = edge2End.lat;
        
        const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
        if (Math.abs(denominator) < 1e-10) return false; // Parallel lines
        
        const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denominator;
        const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denominator;
        
        return t >= 0 && t <= 1 && u >= 0 && u <= 1;
    }

    private isPointInPolygon(point: google.maps.LatLngLiteral, polygon: google.maps.LatLngLiteral[]): boolean {
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            if (((polygon[i].lat > point.lat) !== (polygon[j].lat > point.lat)) &&
                (point.lng < (polygon[j].lng - polygon[i].lng) * (point.lat - polygon[i].lat) / (polygon[j].lat - polygon[i].lat) + polygon[i].lng)) {
                inside = !inside;
            }
        }
        return inside;
    }

    private isPolygonInRegion(path: google.maps.LatLngLiteral[]): boolean {
        if (!this.selectedRegionObject) return true;
        
        const region = this.regions.find(r => r.name === this.selectedRegionObject.name);
        if (!region) return true;
        
        for (const point of path) {
            if (point.lat > region.bounds.north || 
                point.lat < region.bounds.south ||
                point.lng > region.bounds.east ||
                point.lng < region.bounds.west) {
                return false;
            }
        }
        return true;
    }

    /**
     * Check if a single point is within the selected region's bounds.
     * Used by snapping functions to prevent snapping points outside the country border.
     */
    private isInRegionBounds(point: google.maps.LatLngLiteral): boolean {
        if (!this.selectedRegionObject) return true;
        const region = this.regions.find((r: any) => r.name === this.selectedRegionObject.name);
        if (!region || !region.bounds) return true;
        return point.lat <= region.bounds.north &&
               point.lat >= region.bounds.south &&
               point.lng <= region.bounds.east &&
               point.lng >= region.bounds.west;
    }

    /**
     * Get the effective processing distance for enrichment/gap-filling/refinement steps.
     * This is capped to maxProcessingDistanceMeters to prevent aggressive polygon deformation
     * at high snap distances. Vertex-to-vertex snapping still uses the full snapDistanceMeters.
     */
    private getProcessingDistance(): number {
        return Math.min(this.snapDistanceMeters, this.maxProcessingDistanceMeters);
    }

    /**
     * Clamp points that are outside the selected region's bounds to the nearest point on the boundary.
     * Returns a count of how many points were adjusted.
     */
    private clampPointsToRegion(path: google.maps.LatLngLiteral[]): { path: google.maps.LatLngLiteral[], adjustedCount: number } {
        if (!this.selectedRegionObject) return { path, adjustedCount: 0 };
        
        const region = this.regions.find(r => r.name === this.selectedRegionObject.name);
        if (!region || !region.bounds) return { path, adjustedCount: 0 };
        
        let adjustedCount = 0;
        const clampedPath = path.map(point => {
            let lat = point.lat;
            let lng = point.lng;
            let wasAdjusted = false;
            
            // Clamp latitude to bounds
            if (lat > region.bounds.north) {
                lat = region.bounds.north;
                wasAdjusted = true;
            } else if (lat < region.bounds.south) {
                lat = region.bounds.south;
                wasAdjusted = true;
            }
            
            // Clamp longitude to bounds
            if (lng > region.bounds.east) {
                lng = region.bounds.east;
                wasAdjusted = true;
            } else if (lng < region.bounds.west) {
                lng = region.bounds.west;
                wasAdjusted = true;
            }
            
            if (wasAdjusted) {
                adjustedCount++;
            }
            
            return { lat, lng };
        });
        
        return { path: clampedPath, adjustedCount };
    }

    /**
     * Zooms the map to fit the territory's polygon bounds when in edit mode.
     * This provides a better user experience by centering on the specific territory
     * instead of the entire region.
     */
    private zoomToTerritoryPolygon(): void {
        if (!this.mapRef?.googleMap || this.currentPolygon.length === 0) {
            return;
        }

        // Calculate the bounds of the polygon
        const bounds = new google.maps.LatLngBounds();
        this.currentPolygon.forEach(coord => {
            bounds.extend(new google.maps.LatLng(coord.lat, coord.lng));
        });

        // Fit the map to the polygon bounds with some padding
        this.mapRef.googleMap.fitBounds(bounds, {
            top: 50,
            right: 50,
            bottom: 50,
            left: 50
        });

        console.log('Zoomed to territory polygon bounds');
    }

    private drawCountryBorder(): void {
        // Remove existing border if any
        if (this.countryBorderRectangle) {
            this.countryBorderRectangle.setMap(null);
        }

        if (!this.selectedRegionObject || !this.mapRef?.googleMap) {
            return;
        }

        const region = this.regions.find(r => r.name === this.selectedRegionObject.name);
        if (!region || !region.bounds) {
            return;
        }

        // Create a rectangle to show the country boundary
        this.countryBorderRectangle = new google.maps.Rectangle({
            bounds: {
                north: region.bounds.north,
                south: region.bounds.south,
                east: region.bounds.east,
                west: region.bounds.west
            },
            strokeColor: '#FF6B6B',
            strokeOpacity: 0.8,
            strokeWeight: 3,
            fillColor: 'transparent',
            fillOpacity: 0,
            map: this.mapRef.googleMap,
            clickable: false,
            editable: false
        });
    }

    // Polygon editing methods
    startPolygonEditing(): void {
        if (this.currentPolygon.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'No Polygon',
                detail: 'Please draw a polygon first before editing'
            });
            return;
        }

        this.isPolygonEditingMode = true;
        this.lastValidPath = [...this.currentPolygon];
        this.createEditingOverlay();
        
        const snapMessage = this.snapToEdge 
            ? ' Snap to boundaries automatically when you stop editing.' 
            : '';
        this.messageService.add({
            severity: 'info',
            summary: 'Edit Mode',
            detail: 'Polygon editing enabled. Drag vertices to modify the shape.' + snapMessage + ' Overlaps will be prevented.'
        });
    }

    stopPolygonEditing(): void {
        if (!this.isPolygonEditingMode) return;

        // Sync current polygon from overlay so we validate what is actually on the map
        if (this.editingOverlay) {
            this.currentPolygon = [...this.getPathFromOverlay(this.editingOverlay)];
        }
        
        // Apply light snapping to the edited polygon (only snap existing vertices, no new nodes)
        if (this.snapToEdge && this.existingTerritories.length > 0) {
            this.messageService.add({
                severity: 'info',
                summary: 'Applying Snapping',
                detail: 'Snapping vertices to boundaries...',
                life: 1500
            });
            
            // Light snapping - only snap existing vertices without adding intermediate points
            const originalPath = [...this.currentPolygon];
            const snappedPath = this.applyLightSnapping(this.currentPolygon);
            
            // Only accept snapped path if it is still valid (otherwise keep current shape)
            if (this.validateEditModePolygon(snappedPath)) {
                this.currentPolygon = [...snappedPath];
                this.lastValidPath = [...snappedPath];
                console.log(`Applied light snapping: ${originalPath.length} points → ${snappedPath.length} points (no extra nodes)`);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Snapping Complete',
                    detail: `Snapped ${snappedPath.length} vertices to boundaries`,
                    life: 2000
                });
            } else {
                console.log('Light snapping would make polygon invalid - keeping current path');
            }
        }
        
        // Check if polygon is outside region bounds - if so, clamp nodes to nearest border
        const isInRegion = this.isPolygonInRegion(this.currentPolygon);
        if (!isInRegion) {
            // Clamp nodes outside the border to the nearest border point
            const clampedResult = this.clampPointsToRegion(this.currentPolygon);
            if (clampedResult.adjustedCount > 0) {
                this.currentPolygon = clampedResult.path;
                this.lastValidPath = clampedResult.path;
                
                // Update the overlay with clamped points
                if (this.editingOverlay) {
                    this.isRevertingOverlay = true;
                    try {
                        const clampedOverlayPath = clampedResult.path.map(point => new google.maps.LatLng(point.lat, point.lng));
                        this.editingOverlay.setPath(clampedOverlayPath);
                    } finally {
                        setTimeout(() => { this.isRevertingOverlay = false; }, 50);
                    }
                }
                
                this.messageService.add({
                    severity: 'info',
                    summary: 'Nodes Adjusted',
                    detail: `${clampedResult.adjustedCount} node(s) outside the border have been moved to the nearest border point.`,
                    life: 3000
                });
            }
        }
        
        // Validate the final polygon (after clamping if needed)
        if (!this.validateEditModePolygon(this.currentPolygon)) {
            // Only show warning for overlaps now (outside border is auto-fixed)
            const isInRegionAfterClamp = this.isPolygonInRegion(this.currentPolygon);
            if (isInRegionAfterClamp) {
                // Stay in edit mode so the user can fix overlaps
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Cannot stop yet',
                    detail: 'Polygon has overlaps with other territories. Move vertices to fix overlaps, then click Stop Editing again. Your current shape is kept.',
                    life: 6000
                });
            } else {
                // This shouldn't happen after clamping, but handle it just in case
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Cannot stop yet',
                    detail: 'Some nodes are still outside the country boundary. Please adjust them manually.',
                    life: 6000
                });
            }
            return;
        }
        
        // Valid: exit edit mode and cleanup
        this.isPolygonEditingMode = false;
        this.cleanupEditingOverlay();
        this.messageService.add({
            severity: 'success',
            summary: 'Edit Complete',
            detail: 'Polygon editing completed successfully. Click Save to apply final validation.'
        });
    }

    /**
     * Light snapping for edit mode - only snaps existing vertices, doesn't add intermediate points
     * This prevents node explosion when editing territories
     */
    private applyLightSnapping(path: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
        console.log('Applying light snapping to', path.length, 'vertices');
        
        const snappedPath = path.map(point => {
            // Try to snap this vertex to nearby boundary
            const snappedPoint = this.findNearestSnapPoint(point);
            return snappedPoint || point;
        });
        
        // Remove any duplicate points that might have been created by snapping
        return this.cleanupDuplicatePoints(snappedPath);
    }

    private createEditingOverlay(): void {
        if (!this.mapRef?.googleMap || !this.currentPolygon.length) return;

        this.editingOverlay = new google.maps.Polygon({
            paths: this.currentPolygon,
            editable: true,
            draggable: true,
            strokeColor: '#00FF00',
            strokeWeight: 3,
            fillColor: '#00FF00',
            fillOpacity: 0.15,
            zIndex: 1000,
        });

        this.editingOverlay.setMap(this.mapRef.googleMap);

        // Add event listeners for path changes with snapping
        const path = this.editingOverlay.getPath();
        
        // Listen for vertex movements, insertions, and deletions
        this.editingOverlayListeners.push(
            path.addListener('set_at', (index: number) => {
                this.onPolygonVertexDragged(index);
            }),
            path.addListener('insert_at', () => this.onPolygonPathChange()),
            path.addListener('remove_at', () => {
                this.onPolygonPathChange();
                // Ensure minimum 3 points
                if (path.getLength() < 3) {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Minimum Points Required',
                        detail: 'A polygon must have at least 3 points. Cannot delete more nodes.',
                        life: 2000
                    });
                    // Restore the polygon from last valid path (use guard so setPath-induced events are ignored)
                    setTimeout(() => {
                        if (this.editingOverlay && this.lastValidPath.length >= 3) {
                            this.revertOverlayToLastValidPath();
                        }
                    }, 100);
                }
            }),
            this.editingOverlay.addListener('dragend', () => this.onPolygonPathChange())
        );
        
        // Add visual indicator for nodes that can be deleted
        this.setupNodeDeletionInstructions();
    }
    
    private setupNodeDeletionInstructions(): void {
        this.messageService.add({
            severity: 'info',
            summary: 'Edit Mode Active',
            detail: 'Tips: Right-click a vertex to delete it, drag to move, or click an edge to add a vertex',
            life: 4000
        });
    }

    private onPolygonVertexDragged(index: number): void {
        if (!this.editingOverlay || this.isRevertingOverlay) {
            return;
        }
        
        // Get the current path and validate it
        const newPath = this.getPathFromOverlay(this.editingOverlay);
        
        if (!this.snapToEdge) {
            // No snapping - just validate for overlaps
            if (this.validateEditModePolygon(newPath)) {
                this.currentPolygon = [...newPath];
                this.lastValidPath = [...newPath];
                console.log('Vertex moved successfully - no overlaps');
            } else {
                // Movement caused overlap or went outside bounds - revert to last valid path
                this.revertOverlayToLastValidPath();
                const isInRegion = this.isPolygonInRegion(newPath);
                this.messageService.add({
                    severity: 'error',
                    summary: isInRegion ? 'Overlap Detected' : 'Outside Boundary',
                    detail: isInRegion 
                        ? 'Cannot move vertex to this position - would overlap with existing territory.'
                        : 'Cannot move vertex outside the country boundary (shown in red).',
                    life: 2000
                });
                
                console.log('Move blocked - ' + (isInRegion ? 'would cause overlap' : 'outside country bounds') + ', reverted to last valid state');
            }
            return;
        }

        // Snapping enabled - try to snap the dragged vertex
        const path = this.editingOverlay.getPath();
        const movedVertex = path.getAt(index);
        const originalPoint: google.maps.LatLngLiteral = {
            lat: movedVertex.lat(),
            lng: movedVertex.lng()
        };

        // Find nearest snap point
        const snappedPoint = this.findNearestSnapPoint(originalPoint);

        if (snappedPoint && (snappedPoint.lat !== originalPoint.lat || snappedPoint.lng !== originalPoint.lng)) {
            console.log('Vertex', index, 'snapped from', originalPoint, 'to', snappedPoint);
            
            // Apply the snap
            path.setAt(index, new google.maps.LatLng(snappedPoint.lat, snappedPoint.lng));
            
            // Get the path after snapping and validate
            const snappedPath = this.getPathFromOverlay(this.editingOverlay);
            
            if (this.validateEditModePolygon(snappedPath)) {
                this.currentPolygon = [...snappedPath];
                this.lastValidPath = [...snappedPath];
                console.log('Vertex snapped successfully - no overlaps');
            } else {
                // Snap caused overlap or went outside bounds - revert to last valid path
                this.revertOverlayToLastValidPath();
                const isInRegion = this.isPolygonInRegion(snappedPath);
                this.messageService.add({
                    severity: 'error',
                    summary: isInRegion ? 'Overlap Detected' : 'Outside Boundary',
                    detail: isInRegion 
                        ? 'Cannot snap to this position - would overlap with existing territory.'
                        : 'Cannot move vertex outside the country boundary (shown in red).',
                    life: 2000
                });
                
                console.log('Snap blocked - ' + (isInRegion ? 'would cause overlap' : 'outside country bounds') + ', reverted to last valid state');
            }
        } else {
            // No snap found - validate the dragged position
            if (this.validateEditModePolygon(newPath)) {
                this.currentPolygon = [...newPath];
                this.lastValidPath = [...newPath];
                console.log('Vertex moved successfully - no overlaps');
            } else {
                // Movement caused overlap or went outside bounds - revert to last valid path
                this.revertOverlayToLastValidPath();
                const isInRegion = this.isPolygonInRegion(newPath);
                this.messageService.add({
                    severity: 'error',
                    summary: isInRegion ? 'Overlap Detected' : 'Outside Boundary',
                    detail: isInRegion 
                        ? 'Cannot move vertex to this position - would overlap with existing territory.'
                        : 'Cannot move vertex outside the country boundary (shown in red).',
                    life: 2000
                });
                
                console.log('Move blocked - ' + (isInRegion ? 'would cause overlap' : 'outside country bounds') + ', reverted to last valid state');
            }
        }
    }

    private onPolygonPathChange(): void {
        if (!this.editingOverlay || this.isRevertingOverlay) return;

        const newPath = this.getPathFromOverlay(this.editingOverlay);
        
        // Strict validation - prevent any overlaps or going outside bounds
        if (this.validateEditModePolygon(newPath)) {
            this.currentPolygon = [...newPath];
            this.lastValidPath = [...newPath];
            console.log('Path change validation passed');
        } else {
            // Revert to last valid path immediately
            this.revertOverlayToLastValidPath();
            
            // Show error feedback
            const isInRegion = this.isPolygonInRegion(newPath);
            this.messageService.add({
                severity: 'error',
                summary: isInRegion ? 'Overlap Detected' : 'Outside Boundary',
                detail: isInRegion 
                    ? 'Cannot overlap with existing territories. Reverting to last valid state.'
                    : 'Cannot move polygon outside the country boundary (shown in red). Reverting to last valid state.',
                life: 3000
            });
            
            console.log('Path change validation failed - ' + (isInRegion ? 'overlap detected' : 'outside country bounds') + ' - reverted to last valid state');
        }
    }

    /**
     * Revert the editing overlay to lastValidPath. Uses a guard so that any set_at/dragend
     * events fired by setPath() do not re-enter validation and overwrite state.
     * Also syncs currentPolygon so the template polygon (if visible) matches the overlay.
     */
    private revertOverlayToLastValidPath(): void {
        if (!this.editingOverlay || this.lastValidPath.length === 0) return;
        this.isRevertingOverlay = true;
        try {
            const newOverlayPath = this.lastValidPath.map(point => new google.maps.LatLng(point.lat, point.lng));
            this.editingOverlay.setPath(newOverlayPath);
            // Keep currentPolygon in sync so visuals match even if template polygon is shown
            this.currentPolygon = [...this.lastValidPath];
        } finally {
            // Clear guard after path events have been processed (use small delay for safety)
            setTimeout(() => { this.isRevertingOverlay = false; }, 50);
        }
    }
    
    // Very strict validation for edit mode to prevent overlaps
    private validateEditModePolygon(path: google.maps.LatLngLiteral[]): boolean {
        // Require at least 3 points for a valid polygon
        if (!path || path.length < 3) {
            console.log('Edit blocked: Polygon has fewer than 3 points');
            return false;
        }
        // Check if polygon is within country bounds
        if (!this.isPolygonInRegion(path)) {
            console.log('Edit blocked: Polygon is outside country bounds');
            return false;
        }
        
        // During editing, strictly prevent overlaps
        for (const existingTerritory of this.existingTerritories) {
            if (this.isEditMode && this.editingTerritoryId && existingTerritory._id === this.editingTerritoryId) {
                continue;
            }
            
            // Check if territories are adjacent (shared boundaries only) - now using stricter criteria
            const isAdjacent = this.areTerritoriesAdjacent(path, existingTerritory.coordinates);
            
            if (!isAdjacent) {
                // Not adjacent - check for ANY overlap
                let pointsInside = 0;
                
                for (const point of path) {
                    // Skip points on boundary
                    if (!this.isPointOnBoundary(point, existingTerritory.coordinates)) {
                        if (this.isPointInsidePolygon(point, existingTerritory.coordinates)) {
                            pointsInside++;
                        }
                    }
                }
                
                // Block if ANY points are truly inside another territory
                if (pointsInside > 0) {
                    console.log('Edit blocked: Non-adjacent overlap detected -', pointsInside, 'points inside', existingTerritory.name || existingTerritory._id);
                    return false;
                }
                
                // Also check reverse - if the other territory is inside this one
                let reverseInside = 0;
                for (const point of existingTerritory.coordinates) {
                    if (!this.isPointOnBoundary(point, path)) {
                        if (this.isPointInsidePolygon(point, path)) {
                            reverseInside++;
                        }
                    }
                }
                
                if (reverseInside > 0) {
                    console.log('Edit blocked: Existing territory overlaps this one -', reverseInside, 'points inside from', existingTerritory.name || existingTerritory._id);
                    return false;
                }
            }
        }
        
        return true;
    }

    private getPathFromOverlay(overlay: google.maps.Polygon): google.maps.LatLngLiteral[] {
        return overlay.getPath().getArray().map(point => ({
            lat: point.lat(),
            lng: point.lng()
        }));
    }

    // User management methods
    openUserDrawer(): void {
        // Set the user's actual subscription context for the interceptor
        this.setUserSubscriptionContext();
        // Refresh user list before opening drawer
        this.showUserDrawer = true;
    }

    closeUserDrawer(): void {
        this.showUserDrawer = false;
        // Reset search query when closing
        this.userSearchQuery = '';
    }



    onUserSearchChange(): void {
        if (this.userSearchQuery.trim()) {
            const query = this.userSearchQuery.toLowerCase();
            console.log('Searching users with query:', query);
            
            // Let the interceptor handle the subscriber ID via selectedSubscriberId
            const currentUser = this.authService.getLoggedInUser();
            console.log('Searching users - letting interceptor handle subscriber context');
            
            // Set the user's actual subscription context for the interceptor
            this.setUserSubscriptionContext();
            
            // Use paged users with search filter
            this.userService.getPagedUsers({
                pageIndex: 0,
                pageSize: 1000,
                filters: {
                    searchText: query
                }
            }).subscribe({
                next: (response) => {
                    if (response.data && response.data.length > 0) {
                        // Filter only approved, active, non-archived users with FSR role
                        const approvedUsers = response.data.filter(user => 
                            user.status === 'APPROVED' && 
                            user.roleId?.identification === 'FSR' &&
                            user.isActive === true &&
                            user.isArchived === false
                        );
                        this.availableUsers = approvedUsers;
                        console.log('Search results:', this.availableUsers.length);
                    } else {
                        this.availableUsers = [];
                    }
                },
                error: (error) => {
                    console.error('Error searching users:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Search Error',
                        detail: 'Failed to search users. Please try again.'
                    });
                }
            });
        } else {
            // Reset to show all available users
            this.loadAvailableUsers();
        }
    }

    addUserToTerritory(user: User): void {
        if (!user || !user._id) {
            console.error('Invalid user object provided to addUserToTerritory');
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Invalid user data provided'
            });
            return;
        }
        
        if (this.selectedUsers.find(u => u._id === user._id)) {
            this.messageService.add({
                severity: 'warn',
                summary: 'User Already Added',
                detail: `${user.firstName} ${user.lastName} is already assigned to this territory`
            });
            return;
        }
        
        this.selectedUsers.push(user);
        console.log('User added to territory via drawer:', user);
        this.messageService.add({
            severity: 'success',
            summary: 'User Added',
            detail: `${user.firstName} ${user.lastName} added to territory`
        });
    }

    removeUserFromTerritory(userId: string): void {
        if (!userId) {
            console.error('Invalid user ID provided to removeUserFromTerritory');
            return;
        }
        
        const user = this.selectedUsers.find(u => u._id === userId);
        if (user) {
            this.selectedUsers = this.selectedUsers.filter(u => u._id !== userId);
            console.log('User removed from territory via drawer:', user);
            this.messageService.add({
                severity: 'success',
                summary: 'User Removed',
                detail: `${user.firstName} ${user.lastName} removed from territory`
            });
        } else {
            console.warn('User not found in selected users for removal:', userId);
            this.messageService.add({
                severity: 'warn',
                summary: 'User Not Found',
                detail: 'User not found in territory assignment'
            });
        }
        this.refreshUserAssignment();
    }

   refreshUserAssignment(): void {
    this.loadAssignedUsers(this.selectedUsers);
   }

    // Validate user assignment before saving
    validateUserAssignment(): boolean {
        if (this.selectedUsers.length === 0) {
            return false;
        }

        const validUsers = this.selectedUsers.filter(user => 
            user && user._id && user.firstName && user.lastName && user.email
        );

        if (validUsers.length !== this.selectedUsers.length) {
            console.error('Invalid users found in selection:', 
                this.selectedUsers.filter(user => !user || !user._id || !user.firstName || !user.lastName || !user.email)
            );
            return false;
        }

        return true;
    }


}

